import type { ReactElement } from 'react'

import Link from 'next/link'

import { Container } from '@ui/components/Container'
import { Seo } from '@ui/components/seo'
import { HeroBasic } from '@ui/hero'
import { MarketingLayout } from '@ui/layout/MarketingLayout'

const title = 'Terms of Service'
const description = ''

export function Index() {
  return (
    <>
      <Seo title={title} description={description} />
      <div className="relative bg-[url('/img/our-mission-bg.svg')] bg-cover bg-center pt-[120px]">
        <HeroBasic
          className="!bg-transparent"
          title={
            <span className="font-display tracking-wider text-[#0a2d4a]">
              {title.toUpperCase()}
            </span>
          }
          description="Effective Date: April 6, 2026"
        />
      </div>
      <Container>
        <div className="flex flex-col gap-6 py-20 text-[#0a2d4a]">
          {/* Intro Section */}
          <p>
            ATM.com, Inc. d/b/a ATM.com (referred to herein as “ATM.com”) is a
            US- based business that owns the atm.com website and mobile apps.
            All references to “we”, "us", “our”, “ATM”, “Company”, or this
            "Website" shall be construed to mean ATM.com.
          </p>
          <p>
            These Terms of Service constitute a legally binding agreement that
            explains the terms and conditions that govern your access and use of
            this Website, our online and/or Mobile Application (the
            “Application” or “App”), mobile services, content, features, and
            software accessible through or in connection with this Website
            together with any other service performed for you by us in
            connection with this Website (collectively, the “Services”).
          </p>
          <div className="flex flex-col gap-6 border border-black px-2 py-4 text-[#0a2d4a]">
            <h2 className="font-display text-center text-2xl font-normal text-[#0a2d4a]">
              Consumer Fairness
            </h2>
            <p>
              ATM.com designs the Services to prioritize transparency, voluntary
              participation, and user control.
            </p>
            <p>
              Nothing in these Terms is intended to waive non-waivable consumer
              rights under applicable law.
            </p>
            <p>
              Where these Terms permit discretion, ATM.com exercises that
              discretion consistent with principles of fairness and regulatory
              compliance.
            </p>
          </div>
          <p>
            ATM.com® is a registered trademark for the Services, licensed for
            our use by ATM.com, Inc.
          </p>
          <p>
            BY ACCESSING OR USING THE SERVICES, OR BY CLICKING A BUTTON OR
            CHECKING A BOX MARKED “I ACCEPT”, “I AGREE”, OR SIMILAR MARKING, YOU
            SIGNIFY THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY
            THESE TERMS (THE “TERMS” OR “AGREEMENT”), AND TO OUR PRIVACY POLICY
            LINKED AT THE BOTTOM OF OUR WEBSITE’S AND MOBILE APP’S PAGES.
            ACCEPTANCE OF THESE TERMS AND ACCESS TO THE SERVICES IS STRICTLY
            LIMITED TO INDIVIDUALS WHO ARE LEGAL RESIDENTS OF, AND PHYSICALLY
            LOCATED WITHIN, THE FIFTY (50) UNITED STATES AND THE DISTRICT OF
            COLUMBIA AND ARE OF AGE EIGHTEEN (18) YEARS OR OLDER. IF YOU ARE
            ATTEMPTING TO ENTER INTO THIS AGREEMENT ON BEHALF OF A COMPANY OR
            OTHER LEGAL ENTITY, ATM.COM IS UNWILLING TO ALLOW YOU TO OPEN AN
            ACCOUNT AND TO USE THE SERVICES, WHEREUPON YOU SHOULD DISCONTINUE
            THE REGISTRATION PROCESS.
          </p>
          <p className="font-bold">
            PLEASE READ THESE TERMS CAREFULLY TO ENSURE THAT YOU UNDERSTAND EACH
            PROVISION. THESE TERMS CONTAIN A MANDATORY INDIVIDUAL ARBITRATION
            AND CLASS ACTION/JURY TRIAL WAIVER PROVISION IN SECTION 22 THAT
            REQUIRES THE USE OF ARBITRATION ON AN INDIVIDUAL BASIS TO RESOLVE
            DISPUTES, RATHER THAN JURY TRIALS OR CLASS ACTIONS. UNLESS YOU OPT
            OUT OF THE AGREEMENT TO ARBITRATE WITHIN 30 DAYS IN ACCORDANCE WITH
            SECTION 22.11 OF THIS AGREEMENT: (1) YOU WILL ONLY BE PERMITTED TO
            PURSUE DISPUTES OR CLAIMS AND SEEK RELIEF AGAINST US THROUGH
            BINDING, FINAL ARBITRATION, WITH LIMITED EXCEPTIONS, AND YOU WAIVE
            YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR OTHER
            REPRESENTATIVE PROCEEDING OR CLASS-WIDE ARBITRATION; AND (2) YOU ARE
            WAIVING YOUR RIGHT TO PURSUE DISPUTES OR CLAIMS AND SEEK RELIEF IN A
            COURT OF LAW AND TO HAVE A JURY TRIAL.
          </p>
          <p>
            As used in these Terms, “You”, “you", "your", “member”, and “user”
            or similar terms mean the person who uses or accesses the Services.
            “Account" or “ATM.com Account” means the account you establish with
            us for the Services. “Bank Account” means your own personal checking
            or savings bank account and associated debit card that you
            voluntarily link to us but that is separate from your ATM.com
            Account and the Services. “Credentials” means username, email
            address, mobile phone number, PIN, password, and any log-in
            information that is used to access the Services.
          </p>
          <p>
            The Services are controlled and offered by ATM.com from the United
            States of America for users located in the fifty United States which
            includes the District of Columbia but excluding Puerto Rico, US
            Virgin Islands, and other United States territories and possessions
            (the “Service Area”). ATM.com is not a financial planner or advisor
            and we make no guarantees or representations that the Services are
            appropriate for you. Those who access or use the Services from
            locations other than the Service Area do so at their own risk and
            are responsible for compliance with local law.
          </p>
          <p>
            While we strive to provide you with the best possible experience,
            please understand that from time to time, unforeseen issues may
            occur that are beyond our control. This could include, but is not
            limited to, service interruptions, bugs, errors, or data loss. In
            such cases, we cannot guarantee that our services will be error-free
            or that all issues can be fixed immediately. However, we are
            committed to addressing and resolving any problems as promptly as
            possible. We ask for your patience as we work to correct these
            issues. By using our services, you acknowledge that, limited to the
            extent permitted by law, we are not liable for any damages resulting
            from these unforeseeable circumstances, though we will make every
            reasonable effort to minimize disruptions and correct problems to
            the best of our ability while realizing that there are some issues
            that may not be able to be resolved.
          </p>
          {/* Table of Contents Section */}
          <h2
            id="toc"
            className="font-display text-2xl font-normal text-[#0a2d4a]"
          >
            Table of Contents
          </h2>
          <ol className="toc list-decimal pl-4">
            <li>
              <Link href="#about-atm" className="text-blueberry-700 underline">
                About ATM.com; Services
              </Link>
            </li>
            <li>
              <Link
                href="#how-fees-work"
                className="text-blueberry-700 underline"
              >
                How Fees Work
              </Link>
            </li>
            <li>
              <Link
                href="#clearing-house"
                className="text-blueberry-700 underline"
              >
                Your Authorization For Automated Clearing House (Ach) And Other
                Debits & Credits
              </Link>
            </li>
            <li>
              <Link
                href="#electronic-fund-transfer"
                className="text-blueberry-700 underline"
              >
                Electronic Fund Transfer
              </Link>
            </li>
            <li>
              <Link
                href="#anti-money-laundering"
                className="text-blueberry-700 underline"
              >
                Anti-Money Laundering / Sanctions / Compliance With Law
              </Link>
            </li>
            <li>
              <Link
                href="#error-resolution"
                className="text-blueberry-700 underline"
              >
                Error Resolution
              </Link>
            </li>
            <li>
              <Link
                href="#third-party-information"
                className="text-blueberry-700 underline"
              >
                Collection and Use of Third-Party Financial Information
              </Link>
            </li>
            <li>
              <Link
                href="#age-restrictions"
                className="text-blueberry-700 underline"
              >
                Eligibility And Age Restrictions; Your Account And Prohibited
                Conduct
              </Link>
            </li>
            <li>
              <Link
                href="#electronic-signatures"
                className="text-blueberry-700 underline"
              >
                Consent To Use Electronic Signatures And Electronic
                Communications
              </Link>
            </li>
            <li>
              <Link href="#messaging" className="text-blueberry-700 underline">
                Messaging
              </Link>
            </li>
            <li>
              <Link
                href="#security-incident-response"
                className="text-blueberry-700 underline"
              >
                Security And Incident Response
              </Link>
            </li>
            <li>
              <Link
                href="#chatbots-ai"
                className="text-blueberry-700 underline"
              >
                Use Of Chatbots And Artificial Intelligence
              </Link>
            </li>
            <li>
              <Link
                href="#service-availability"
                className="text-blueberry-700 underline"
              >
                Service Availability
              </Link>
            </li>
            <li>
              <Link
                href="#legal-compliance"
                className="text-blueberry-700 underline"
              >
                Legal Compliance
              </Link>
            </li>
            <li>
              <Link
                href="#indemnification"
                className="text-blueberry-700 underline"
              >
                Indemnification
              </Link>
            </li>
            <li>
              <Link href="#disclaimer" className="text-blueberry-700 underline">
                Disclaimer Of Representations And Warranties
              </Link>
            </li>
            <li>
              <Link href="#liability" className="text-blueberry-700 underline">
                Limitation Of Liability
              </Link>
            </li>
            <li>
              <Link
                href="#legal-notices"
                className="text-blueberry-700 underline"
              >
                Legal Notices And Special Disclaimers
              </Link>
            </li>
            <li>
              <Link
                href="#proprietary-content"
                className="text-blueberry-700 underline"
              >
                Atm.Com Proprietary Content
              </Link>
            </li>
            <li>
              <Link href="#copyright" className="text-blueberry-700 underline">
                Copyright Infringement Claims
              </Link>
            </li>
            <li>
              <Link
                href="#application-license"
                className="text-blueberry-700 underline"
              >
                Application License And Device Manufacturer Terms
              </Link>
            </li>
            <li>
              <Link
                href="#mandatory-waiver"
                className="text-blueberry-700 underline"
              >
                Mandatory Arbitration And Class Action Waiver
              </Link>
            </li>
            <li>
              <Link href="#taxes" className="text-blueberry-700 underline">
                Taxes
              </Link>
            </li>
            <li>
              <Link href="#assignment" className="text-blueberry-700 underline">
                Assignment
              </Link>
            </li>
            <li>
              <Link
                href="#governing-law"
                className="text-blueberry-700 underline"
              >
                Governing Law
              </Link>
            </li>
            <li>
              <Link
                href="#other-terms"
                className="text-blueberry-700 underline"
              >
                Other Terms
              </Link>
            </li>
            <li>
              <Link
                href="#privacy-policy"
                className="text-blueberry-700 underline"
              >
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link href="#california" className="text-blueberry-700 underline">
                California Residents
              </Link>
            </li>
            <li>
              <Link
                href="#survivability"
                className="text-blueberry-700 underline"
              >
                Survivability
              </Link>
            </li>
            <li>
              <Link
                href="#contact-information"
                className="text-blueberry-700 underline"
              >
                Atm.Com Contact Information
              </Link>
            </li>
          </ol>
          {/* About Section */}
          <h2
            id="about-atm"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            1. ABOUT ATM.COM; SERVICES
          </h2>
          <p>
            ATM.com provides technology-enabled financial wellness services. The
            legal classification of financial products may change over time as
            laws evolve.
          </p>
          <p>
            These Terms describe how the Services function economically and
            operationally. Nothing in these Terms waives rights or protections
            required by law.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.1 General
          </h3>
          <p>
            To access and use the Services, you must register and create a user
            account. See Section 1.6 for details regarding identify verification
            and Section 8.1for eligibility requirements.{' '}
          </p>
          <p>
            ATM.com helps you earn money by completing Offers (as described
            below in Section 1.2) in numerous ways, without ATM.com selling your
            personal information (our limited rights to share your personal
            information are described in our Privacy Policy). ATM.com also
            offers cash advances to qualified users.
          </p>
          <p>
            If you leave the ATM.com App through a link to an external source,
            it is up to you to decide what information you wish to share with
            third-party brands when calling, shopping, buying, and filling out
            forms or questionnaires. While ATM.com does not share your personal
            information with other companies, if you choose to start a survey in
            exchange for Offers, limited personal information may be shared with
            our third-party survey provider in order to better qualify you for a
            survey and maximize your earnings.
          </p>
          <p>
            Your earnings will be recorded in your ATM.com Account. Completed
            Offers may be recorded instantly but some may take up to 60 days
            based on reporting and tracking limitations with third party
            companies and networks.
          </p>
          <p>
            To protect our User community from fraud and bad acts, including
            money laundering, your funds will be in an unearned state and not
            considered earned until your ATM.com Account balance reaches a
            threshold amount (the “Minimum Account Balance”). This Minimum
            Account Balance is determined at the discretion of ATM.com and may
            vary from account to account. Once your earned Account balance
            reaches the Minimum Account Balance, you can withdraw funds in
            excess of it. We reserve the right to waive or modify this
            withdrawal policy at our discretion. We keep this Minimum Account
            Balance policy to withdraw cash as a way to protect you and/or the
            User community from fraud and in turn make payments faster. As
            another fraud protection measure, new users will not be allowed to
            withdraw any funds for 48 hours after they become active.
          </p>
          <p>
            All available user funds, including, but not limited to, offer
            payouts and earnings, will be held in your ATM.com Account
            indefinitely until you choose to withdraw some or all of your
            ATM.com Account balance.
          </p>
          <p>
            We will generally transfer the requested funds from your ATM.com
            Account to your personal bank account that you have linked to your
            ATM.com Account (together with that bank account’s associated debit
            card, your “Bank Account”) within four to six banking business days
            of when we receive your request. It is important for you to know the
            amount of available funds in your ATM.com Account before instructing
            us to transfer funds from your ATM.com Account to your Bank Account.
            If you do not have sufficient available funds in your ATM.com
            Account to cover the amount of the requested transfer, your request
            for the transfer will be declined.
          </p>
          <p>
            Users may also take advantage of the Instant Withdrawal feature
            which enables users to withdraw funds directly to their linked debit
            card – a transaction which will happen immediately upon submission.
            This feature is subject to withdrawal minimums and also to a
            transaction fee which will be disclosed to the User prior to
            completion of the transaction request.
          </p>
          <p>
            We may also transfer funds from your ATM.com Account to your Bank
            Account without notice to you upon the closure of your ATM.com
            Account as described below and at any time, including if required by
            applicable law, or if we, in our sole discretion, suspect the
            Services are being used for illicit purposes or otherwise in
            violation of these Terms.
          </p>
          <p>
            See Section 2.3 for details relating to account closures and
            deletions.
          </p>
          <p>
            ATM.COM DOES NOT IN ANY WAY WARRANT OR GUARANTEE THE SUCCESS OF ANY
            ACTION WHICH YOU TAKE IN RELIANCE ON THE INFORMATION PROVIDED AT
            THIS WEBSITE OR THE APP. WE DO NOT WARRANT THAT YOU WILL MAKE ANY
            MONEY OR THAT YOU WILL NOT LOSE MONEY. ATM.COM IS NOT A FINANCIAL
            ADVISER, AND THE SERVICES ARE NOT INTENDED TO PROVIDE FINANCIAL
            ADVICE. WE ARE NOT AN INVESTMENT ADVISOR, WE DO NOT PURPORT TO AND
            DO NOT, IN ANY FASHION, PROVIDE INVESTMENT ADVICE, TAX, ACCOUNTING,
            ACTUARIAL, RECORDKEEPING, LEGAL, OR ANY RELATED SERVICES OR ADVICE.
            WE ARE NOT RESPONSIBLE FOR ENSURING YOUR BANK ACCOUNT HAS SUFFICIENT
            FUNDS FOR YOUR NEEDS, PURPOSES, OR TRANSACTIONS. WE DO NOT MAKE ANY
            REPRESENTATIONS, WARRANTIES, OR GUARANTEES OF ANY KIND THAT THE
            SERVICES ARE APPROPRIATE FOR YOU. BEFORE USING THE SERVICES, YOU
            SHOULD CONSIDER OBTAINING ADDITIONAL INFORMATION AND ADVICE FROM A
            FINANCIAL ADVISER.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.2 Basic Services
          </h3>
          <p>
            ATM.com offers the following Basic Services to users at no cost -
            there are no fees associated with accessing these features of the
            App:
          </p>
          <p>
            <span className="block underline">A. Offers</span>
            ATM.com pays our users as compensation for actions leading to
            ATM.com receiving revenue from the advertising brands we partner
            with (“Offers”). Actions can include things like placing calls with
            a minimum duration, receiving a quote, going to third-party websites
            to shop, buying, ordering services, filling out lead forms, playing
            games, or responding to surveys. Offers are paid to you directly
            from ATM.com. The companies themselves are not paying you, but
            instead, ATM.com derives advertising revenue from the companies and
            brands that you successfully interact with via the App. A portion of
            ATM.com’s advertising revenue is used to pay you for your completed
            actions.
          </p>
          <p>
            For the purpose of delivering contextually relevant Services and
            optimizing commercial outreach, we collect information relating to
            your device's geographical location and proximity (collectively,
            "Geospatial Data"). This Geospatial Data, whether precise or
            general, may be used for purposes including, but not limited to,
            tailoring advertisements, providing personalized promotional offers,
            and customizing service features based on your present or past
            location. You acknowledge this collection is subject to your
            device's operating system permissions, and that controls for
            disabling or opting out of this data collection may be accessed
            through your device settings, as further described below.
          </p>
          <p>
            For Offers that utilize location tracking, users may disable and opt
            out of location tracking permissions by clicking this in-app link –{' '}
            <span>
              <Link href="atmapp://app.navigator/?nav=system_settings"></Link>
              atmapp://app.navigator/?nav=system_settings
            </span>{' '}
            - which will direct the user to the appropriate screen of their
            smart phone settings.
          </p>
          <p>
            <span className="block underline">B. Data Offers</span>
            Another way to earn money with our Services is by answering Data
            Vault Questions. Each day that you log into the App, you will have
            the opportunity to answer a number of questions. For each question
            that you answer, you will earn a small amount of money. Your
            responses may be shared with trusted third parties and used by
            ATM.com to analyze, improve, and enhance your user experience. See
            our Privacy Policy for more information on how we protect your data.
          </p>
          <p>
            <span className="block underline">C. Community Offers</span>
            ATM.com users can earn passive income through Community Offers,
            which are supported by the use of de-identified and statistical data
            derived from our users. We may collect, process, and commercially
            exploit information gathered through the Services, including, but
            not limited to, granular data elements that have been processed to
            be permanently de-identified and anonymized ("Anonymized Data
            Sets"). These Anonymized Data Sets, which may be provided to
            commercial partners on an individual or aggregated basis, are
            leveraged for internal business operations and external purposes,
            including, but not limited to, developing, commercializing, and
            licensing broad data-driven products, predictive models, industry
            trend analysis, strategic business reporting, risk management, and
            the creation of targeted opportunities for our users. All such data
            is processed to ensure it cannot reasonably be used to identify any
            individual user or their device.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.3 ATM Instant Cash&trade;
          </h3>
          <p>
            <span className="block underline">A. Nature of the Service</span>
            ATM Instant Cash is a short-term advance of funds made available
            through the Services against expected future deposits to your linked
            Bank Account.
          </p>
          <p>
            An Advance allows you to receive funds before your next anticipated
            deposit. ATM.com attempts to recover the Advance and applicable fees
            through an authorized debit to your linked Bank Account on or after
            your next expected deposit date, as described below.
          </p>
          <p>
            An Advance is non-recourse. This means ATM.com does not pursue legal
            action, sell debt, or engage third-party debt collectors to compel
            repayment. However, unpaid Advances may affect your eligibility to
            receive future Advances or certain Services.
          </p>
          <p>
            ATM Instant Cash is a financial service feature of the App. It is
            not a deposit account, savings account, or credit card product.
            ATM.com is not a bank.
          </p>
          <p>
            Credits and debits will appear on your card or bank statement with
            the descriptor in some variation of “ATM_com Instant Cash” or
            “ATM_com Monthly Platform Fee”.
          </p>
          <p>
            <span className="block underline">B. Eligibility</span>
            To request an Advance, you must:
          </p>
          <ul className="list-disc pl-6">
            <li>be an individual (not a business entity)</li>
            <li>be at least 18 years old</li>
            <li>
              be a U.S. resident, with a valid Social Security number, located
              within the Service Area
            </li>
            <li>link a qualifying Bank Account</li>
            <li>satisfy ATM.com’s automated eligibility criteria</li>
          </ul>
          <p>
            Eligibility is determined using automated analysis of account
            activity and other risk signals. Approval is discretionary and may
            change at any time.
          </p>
          <p>
            Approval of one Advance does not guarantee approval of future
            Advances.
          </p>
          <p>
            If an Advance is not repaid or is reversed, your eligibility for
            future Advances may be permanently revoked. Your account may be
            restricted, suspended, or terminated, and access to Services may be
            limited. ATM.com may also block access across related accounts,
            devices, or identities.
          </p>
          <p>
            <span className="block underline">C. Amount of Advances</span>
            Advance amounts are determined algorithmically using historical
            deposit and spending patterns and other risk factors.
          </p>
          <p>At its sole discretion, ATM.com may:</p>
          <ul className="list-disc pl-6">
            <li>increase or decrease Advance limits</li>
            <li>restrict frequency</li>
            <li>deny requests</li>
            <li>modify eligibility criteria</li>
          </ul>
          <p>
            <span className="block underline">D. Fees</span>
            Each Advance may include:
          </p>
          <ul className="list-disc pl-6">
            <li>a Transaction Fee (if applicable)</li>
            <li>a Platform Fee (if applicable)</li>
            <li>any voluntary Tip you choose to add</li>
          </ul>
          <p>All fees are disclosed before you confirm the Advance.</p>
          <p>
            Fees are part of the cost of accessing the Instant Cash service and
            are not interest. Fees do not accrue over time.
          </p>
          <p>
            A separate section of these Terms explains how fees begin, recur,
            and stop.
          </p>
          <p>
            <span className="block underline">E. Voluntary Tips</span>
            You may choose to add a voluntary Tip when requesting an Advance.
          </p>
          <p>
            Tips are optional and declining to provide a Tip has no effect on
            approval, eligibility, delivery speed, or access to Services.
          </p>
          <p>A Tip does not:</p>
          <ul className="list-disc pl-6">
            <li>increase approval likelihood</li>
            <li>increase Advance amount</li>
            <li>speed delivery</li>
            <li>affect eligibility</li>
            <li>affect account standing</li>
            <li>affect future approvals</li>
          </ul>
          <p>Declining to add a Tip has no negative consequence.</p>
          <p>
            Tips are treated as voluntary payments and are added only if you
            affirmatively select them.
          </p>
          <p>
            <span className="block underline">F. Disbursement</span>
            Approved Advances are typically delivered within approximately one
            hour, though timing may vary due to bank processing limitations.
          </p>
          <p>
            ATM.com may use micro-deposits or verification transactions to
            confirm account ownership.
          </p>
          <p>Delivery timing is not guaranteed.</p>
          <p>
            <span className="block underline">G. Repayment Authorization</span>
            You authorize ATM.com to attempt repayment through an automated
            debit from your linked Bank Account on or after your next expected
            deposit date.
          </p>
          <p>ATM.com may:</p>
          <ul className="list-disc pl-6">
            <li>retry failed debits</li>
            <li>collect partial payments</li>
            <li>
              recover funds from your ATM.com ledger balance until the Advance
              and associated fees are satisfied.
            </li>
          </ul>
          <p>You may prepay an Advance at any time from within the App.</p>
          <p>
            <span className="block underline">H. Insufficient Funds</span>
            ATM.com attempts to detect sufficient funds before initiating
            repayment but cannot guarantee that overdrafts will not occur.
          </p>
          <p>
            You are responsible for maintaining sufficient funds in your Bank
            Account.
          </p>
          <p>ATM.com is not responsible for overdraft or bank fees.</p>
          <p>
            <span className="block underline">I. Non-Repayment Effects</span>
            If an Advance is not repaid:
          </p>
          <ul className="list-disc pl-6">
            <li>your eligibility for future Advances may be restricted</li>
            <li>certain Services may be limited</li>
            <li>your account may be suspended</li>
          </ul>
          <p>
            ATM.com does not file credit reports, pursue legal judgments, or
            sell unpaid Advances.
          </p>
          <p>
            <span className="block underline">J. Cancellation</span>
            ATM.com may suspend or terminate Instant Cash access to prevent
            fraud, abuse, or misuse.
          </p>
          <p>This includes suspected:</p>
          <ul className="list-disc pl-6">
            <li>identity theft</li>
            <li>account manipulation</li>
            <li>artificial deposit patterns</li>
            <li>evasion of repayment</li>
            <li>violations of law</li>
          </ul>
          <p>
            <span className="block underline">L. Regulatory Compliance</span>
            ATM.com reserves the right to modify, restrict, or discontinue
            Instant Cash to comply with applicable law or regulatory guidance.
          </p>
          <p>
            Nothing in this section waives consumer protections required by law.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.4 How Fees Work
          </h3>
          <p>
            <span className="block underline">A. Types of Fees</span>
            Depending on how you use the Services, the following fees may apply:
          </p>
          <ul className="list-disc pl-6">
            <li>Transaction Fees associated with Instant Cash Advances</li>
            <li>
              Initial Platform Fee (one-time activation fee, if applicable)
            </li>
            <li>Monthly Platform Fee for ongoing platform access</li>
            <li>
              Maintenance or inactivity fees associated with Account status
            </li>
          </ul>
          <p>Not all users incur all fees.</p>
          <p>
            <span className="block underline">B. When Fees Begin</span>
            Fees begin only after you affirmatively confirm a transaction or
            activate a feature that carries a fee.
          </p>
          <p>
            You will see the applicable fee before you complete the action that
            triggers it.
          </p>
          <p>
            <span className="block underline">C. Recurring Fees</span>
            If you activate a feature that includes a recurring Platform Fee:
          </p>
          <ul className="list-disc pl-6">
            <li>the Platform Period begins on the activation date</li>
            <li>the fee recurs monthly on or about the anniversary date</li>
            <li>he fee continues until you pause or cancel the feature</li>
          </ul>
          <p>You may cancel recurring access at any time in the App.</p>
          <p>
            <span className="block underline">D. When Fees Stop</span>
            Recurring fees stop after:
          </p>
          <ul className="list-disc pl-6">
            <li>you cancel the feature, and</li>
            <li>the current Platform Period ends</li>
          </ul>
          <p>
            Canceling does not retroactively refund fees already charged for an
            active period.
          </p>
          <p>Outstanding Advances or fees remain payable after cancellation.</p>
          <p>
            <span className="block underline">
              E. Interaction Between Fees and Advances
            </span>
            Receiving a new Advance during an active Platform Period does not
            restart the Platform Period or duplicate the Platform Fee.
          </p>
          <p>Each Advance carries its own disclosed Transaction Fee.</p>
          <p>
            <span className="block underline">
              F. Inactivity and Maintenance Fees
            </span>
            Inactive Accounts may incur maintenance fees as described elsewhere
            in these Terms.
          </p>
          <p>You can avoid inactivity fees by accessing the Services</p>
          <p>
            <span className="block underline">G. No Hidden Fees</span>
            ATM.com does not charge undisclosed fees. All fees are presented
            before confirmation.
          </p>
          <p>
            <span className="block underline">
              H. Personal Finance and Other Bonus Features
            </span>
            When you apply and are approved for an ATM Instant Cash Advance, you
            are enabled to use our suite of Personal Finance tools at no extra
            cost and will have access to them while your account is in good
            standing.
          </p>
          <p>
            ATM Personal Finance is a set of tools to give users insights into
            their spending to increase their financial health and plan better
            for today and the future. The benefits offered through ATM Personal
            Finance include:
          </p>
          <ul className="list-disc pl-6">
            <li>Spending and cashflow insights</li>
            <li>Budgeting and low balance alerts</li>
            <li>Subscription and bill tracking</li>
            <li>Bank Account monitoring</li>
          </ul>
          <p>
            Other bonus features may be available from time to time. ATM
            reserves the right to modify or cancel bonus features at any time.
          </p>
          <p>
            <span className="block underline">I. General Terms</span>
            Once an ATM Instant Cash transfer is requested, confirmed and agreed
            to, it cannot be cancelled or changed.
          </p>
          <p>
            Your right to stop payment on any preauthorized electronic fund
            transfer from your Bank Account — whether initiated via debit card,
            ACH, or any other electronic means — is not affected by these Terms.
            However, stopping payment on a preauthorized debit does not cancel,
            reduce, or extinguish your obligation to repay an outstanding
            Advance or associated fees. If you stop a payment or initiate a
            chargeback, you remain responsible for repaying the outstanding
            balance through alternative means, including prepayment from within
            the App. ATM.com may exercise any other remedies available under
            these Terms, including suspending your account, withholding ATM.com
            Account balances to offset unpaid amounts, and restricting future
            access to Services.
          </p>
          <p>
            If you have requested to have your account closed and you have an
            outstanding, unsettled Instant Cash Advance, the account closure
            will not be processed until repayment of the Advance has been
            received.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.5 Banking
          </h3>
          <p>
            ATM.com is not a depository institution. ATM.com is not a bank; your
            ATM.com Account is a ledger account. ATM.com is not acting as a
            trustee, fiduciary or escrow with respect to your funds.
          </p>
          <p>
            You must own and be the authorized user of the linked Bank Account.
            You are responsible for ensuring your Bank Account has sufficient
            funds for your needs, purposes, or transactions. By using the
            Services provided by ATM.com, you represent and warrant that you
            will safeguard your Bank Account information and will supervise and
            be completely responsible for any use of your Bank Account by you
            and anyone other than you.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            1.6 Identity Verification
          </h3>
          <p>
            To help the government fight the funding of terrorism and money
            laundering activities, US Federal law requires all financial
            institutions and companies like ours to sometimes obtain, verify,
            and record information that identifies each person as a “customer”
            who opens an account. Identity verification is also needed to
            provide certain Services to you.
          </p>
          <p>
            As part of the verification process to eliminate fraud and for tax
            reporting requirements, when you register for the App or apply for
            ATM Instant Cash or take similar actions, we may ask you for your
            name, date of birth, street address, identification number (like a
            Social Security Number), debit card information, and any other
            information that will allow us to identify you as the customer
            associated with the bank account to be linked. We may also ask you
            to submit copies of your government-issued photo ID, proof of
            residential address, a recent bank statement, or similar
            information. All data will be by transmitted by an independent
            third-party service utilizing secure, encrypted means. Please review
            our Privacy Policy for details.
          </p>
          <p>
            Failure to provide verification of your identity and/or registered
            information when requested may result in the inability to open an
            account, the termination of your ATM.com account or limiting of
            available Services. In the event we have accepted any funds from you
            in connection with an account application or otherwise, you will be
            responsible for providing us a mailing address to which payment of
            any refund can be sent.
          </p>
          <p>
            We may share the information you have provided us, in accordance
            with our Privacy Policy, to our banking or other partners in
            connection with opening and maintaining an account for you.{' '}
          </p>
          <h2
            id="how-fees-work"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            2. FEES; PAUSES AND CANCELLATIONS; CLOSURES; REFUNDS; CHARGEBACKS
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.1 Maintenance and Inactivity
          </h3>
          <p>
            A maintenance fee of $3 per month will be deducted from all users’
            ATM.com Account Balances on a monthly basis. For active users, ATM
            will waive the monthly maintenance fee. (See Section 8.4 for
            additional details including what we mean by “active”). If your
            ATM.com balance is insufficient to cover the full fee at the time of
            your monthly deduction, the remaining balance of repayment will be
            deducted. Fee deductions will not bring an ATM.com Account below a
            $0.00 Account balance. ATM.com maintains the right to waive these
            fees at their discretion.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.2 Payment of Fees
          </h3>
          <p>
            We reserve the right to deduct cash advance repayment funds, or
            payment for any fees (Transaction Fees and Monthly Platform Fees,
            for example) from your ATM.com Account if we are unable to process
            them from your Bank Account.{' '}
          </p>
          <p>
            Platform Fees will be deducted separate and apart from deductions
            for repayment of ATM Instant Cash and associated fees. Receipts for
            repayments to ATM.com will be sent to your email on record and will
            also be visible in the App settings.
          </p>
          <p>
            ATM.com avoids attempting to charge any fees or deduct a repayment
            if we detect that your account does not contain sufficient funds.
          </p>
          <p>
            We monitor your Bank Account balance and do our best to ensure you
            have sufficient funds before debiting your account for the Platform
            Fee, but the company makes no warranties that an overdraft will not
            occur. If our attempt to charge your Bank Account is denied for any
            reason, we reserve the right to make additional subsequent attempts
            when we detect a positive balance in order to collect unpaid
            balances from prior months as well as for the current period. If
            your Bank Account balance is not equal or greater than the amount
            due us, we may implement partial payments until the entire balance
            has been repaid.
          </p>
          <p>
            If our attempts to debit your Bank Account for repayment are
            unsuccessful because of insufficient funds to cover the repayment or
            an invalid account or some other situation, we may notify you by an
            in-app notification, a push notification, an SMS message, email, or
            a combination of those. We also reserve the right to deduct
            repayment funds from your ATM.com Account if we are unable to
            process the payment from your Bank Account.
          </p>
          <p>
            Should an overdraft occur, we are not responsible for any overdraft
            fees, over-the-limit fees, insufficient fund charges, or any other
            bank fees you are charged because you fail to maintain a sufficient
            balance in your Bank Account, including any overdraft resulting from
            a debit initiated by us that had been approved by you.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.3 Account Closures
          </h3>
          <p>
            You may close your account by clicking the Account Deletion icon.
            Once your account is closed, it cannot be reopened but we may waive
            this policy at our discretion.
          </p>
          <p>
            If you close your account and have a remaining balance, ATM.com will
            attempt to return funds to your linked Bank Account after fraud
            review.
          </p>
          <p>
            If funds cannot be returned due to invalid banking or identity
            verification issues, they will be handled under applicable unclaimed
            property laws. ATM.com does not permanently retain returnable user
            funds.
          </p>
          <p>
            You remain responsible for maintaining accurate account information
            to receive disbursements.
          </p>
          <p>
            To collect the final funds when an account is closed, there is a
            delay of 60 days before the transfer to your Account is commenced
            and the actual transfer may then take a few business days. The
            60-day delay is to protect our User community from fraud. Users can
            view their Available Balance by clicking on the Wallet icon in the
            App and their Withdrawable Balance will be displayed. When the
            account is closed, disbursement of the full balance will be
            processed for accounts equal to or over the Minimum Account Balance;
            no disbursements will be made if the Minimum Account Balance is not
            satisfied. We reserve the right to waive or modify the Minimum
            Account Balance policy at our discretion.
          </p>
          <p>
            If your account has been suspended or flagged for potential misuse,
            you will not receive your account balance until your identity has
            been verified by using a secure link that will be presented to you.
          </p>
          <p>
            If you request to have your account closed and you have an
            outstanding, unsettled Instant Cash Advance, the account closure
            will not be processed until repayment of the Advance has been
            received.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.4 Platform Period Pauses and Cancellations
          </h3>
          <p>
            You may pause Platform Periods at any time from within the App. If
            you pause Platform Periods, your access to Personal Finance and
            other bonus features will be discontinued at the end of the final
            covered Platform Period.
          </p>
          <p>
            Pausing your Platform Periods will not alter or stop the scheduled
            repayment of Instant Cash and associated fees.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.5. Refunds; Chargebacks
          </h3>
          <p>Transaction Fees and tips are non-refundable.</p>
          <p>
            See Section 8.7 below for important information and more details
            regarding chargebacks.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            2.6 Errors and Disputes
          </h3>
          <p>
            ATM.com complies with the Electronic Fund Transfer Act of 1978
            (15USC§1693 et seq.) in order to protect users from fraud and
            account errors. The EFTA sets guidelines for how you can be
            protected from unauthorized electronic transactions like debit card
            payments and online transfers. For more information and details
            relating to disputes or errors, please refer to Sections 4, 6, and
            22 below.
          </p>
          <h2
            id="clearing-house"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            3. YOUR AUTHORIZATION FOR AUTOMATED CLEARING HOUSE (ACH) AND OTHER
            DEBITS & CREDITS
          </h2>
          <p>
            By agreeing to these Terms, you authorize ATM.com to electronically
            debit and credit your Bank Account (either a checking or savings
            account) via ACH, bank, or debit card transfers in order to provide
            the Services, including ATM Instant Cash. This authorization
            includes, if ever applicable, correction of erroneous debits and
            credits via ACH, as follows:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Range of Acceptable Debit Amounts: $5 to $50 per transaction;
              Maximum $100 per Business day (as defined below)
            </li>
            <li>Frequency of Debits: Up to one time per Business Day</li>
            <li>
              Effective Date of Transactions: The Business Day on which any ACH
              transaction is scheduled
            </li>
          </ul>
          <p>
            You agree to indemnify and hold harmless ATM.com from and against
            any loss incurred as a result of its withdrawal of a preauthorized
            debit transaction from your Bank Account if any of the information
            relied upon in your request to stop payment is incorrect or
            incomplete.
          </p>
          <p>
            You warrant and represent to ATM.com that you have the right to
            authorize us to charge and credit your Bank Account for payments due
            to us under this Agreement.
          </p>
          <p>
            From time to time, we may offer free trials which require you to
            provide us with your debit card information. If you cancel the free
            trial before the end of the trial period, your debit card will not
            be charged. If you do not cancel before the end of the trial period,
            your debit card will be charged on the last day of the trial period.
            Thereafter, you may cancel at any time in accordance with other
            polices detailed in these Terms.
          </p>
          <p>
            You acknowledge that the electronic authorization contained in this
            Section represents your written authorization for debit or credit
            transactions as provided herein and will remain in full force and
            effect until you notify ATM.com that you wish to revoke this
            authorization by emailing{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            . You must notify ATM.com in order to cancel this authorization. You
            should print a copy of this authorization for your records. ATM.com
            reserves the right to cancel and close your account if this
            authorization is revoked.
          </p>
          <p>
            You may revoke this authorization at any time by emailing{' '}
            <a href="mailto:support@ATM.com" className="underline">
              support@ATM.com
            </a>
            . Revocation will be effective for future preauthorized transfers —
            whether via debit card, ACH, or any other electronic means — after
            ATM.com has had a reasonable opportunity to act on your revocation
            request, consistent with applicable law. Revocation of authorization
            does not cancel, reduce, or extinguish any outstanding obligation to
            repay an Advance or associated fees. If you revoke authorization
            while an Advance or fees remain outstanding, you remain responsible
            for repaying those amounts through alternative means available in
            the App. ATM.com reserves the right to cancel and close your account
            if this authorization is revoked, and may offset any positive
            ATM.com Account balance against outstanding obligations before
            disbursing remaining funds.
          </p>
          <p>
            In addition to any of your other representations and warranties in
            these Terms, you represent that: (a) your browser is equipped with
            at least 128-bit security encryption; (b) you are capable of
            printing, storing, or otherwise saving a copy of this electronic
            authorization for your records; and (c) the ACH transactions you
            hereby authorize comply with applicable law.
          </p>
          <h2
            id="electronic-fund-transfer"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            4. ELECTRONIC FUND TRANSFER
          </h2>
          <p>
            The following disclosures are made in accordance with the federal
            Electronic Fund Transfer Act of 1978 as amended regarding electronic
            payments, deposits, transfers of funds and other electronic
            transfers to and from your account. It is also referred to as the
            Federal Reserve Board Regulation E. Click to view the full text of
            the{' '}
            <span>
              <a
                className="underline"
                href="https://uscode.house.gov/view.xhtml?req=granuleid%3AUSC-prelim-title15-chapter41-subchapter6&edition=prelim"
                target="_blank"
              >
                US Code
              </a>
            </span>{' '}
            or{' '}
            <span>
              <a
                className="underline"
                href="https://www.federalreserve.gov/boarddocs/supmanual/cch/efta.pdf"
                target="_blank"
              >
                Regulation E
              </a>
            </span>
            .
          </p>
          <p>
            There may be limitations on account activity that restrict your
            ability and any such limits are disclosed in the agreements and
            Terms governing your account.
          </p>
          <p>
            The disclosures in this Section 4 apply to all electronic transfers
            including any monthly recurring fees for Services as described in
            these Terms of Service.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.1 Electronic Fund Transfer Definitions
          </h3>
          <p>
            An <span className="underline">Electronic Fund Transfer</span> (EFT)
            is a transfer of funds, other than a transaction originated by
            check, draft or similar paper instrument, that is processed by
            electronic means including, but not limited to, transfers to/from a
            bank account, a debit card and ACH transfers, as instructed by You.
          </p>
          <p>
            A{' '}
            <span className="underline">
              Preauthorized Electronic Fund Transfer
            </span>{' '}
            is an EFT that You have authorized in advance to recur at
            substantially regular intervals, such as a monthly membership or
            Platform Fee.
          </p>
          <p>
            An{' '}
            <span className="underline">
              Unauthorized Electronic Fund Transfer
            </span>{' '}
            is an EFT from Your account initiated by a person other than You and
            from which You receive no benefit – with some exclusions.
          </p>
          <p>
            For the purpose of this Section 4, an Access Device (“Device”) is
            defined as a card, code, or other means of access to a consumer’s
            account or a combination of these used by a consumer to initiate
            EFTs. Devices include debit cards, personal identification numbers
            (PINs), telephone transfer and telephone bill payment codes, and
            other means to initiate an EFT.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.2 Your Liability
          </h3>
          <p>
            Authorized Transfers: You are liable for all EFTs that you
            authorize, directly or indirectly.
          </p>
          <p>
            Unauthorized Transfers: Tell us at once if you believe your account
            or PIN or Access Information (as defined below) is lost or stolen or
            has been or may be subject to unauthorized Electronic Fund
            Transfers. Support message us immediately to keep your possible
            losses to a minimum. You could lose all the money in your
            account(s). If you tell us within two (2) business days after
            learning of the loss or theft of your account access device, or
            after learning of any other unauthorized transfers from your
            account, you can lose no more than $50.00 if Electronic Fund
            Transfers are made without your permission. For these transactions,
            if you DO NOT tell us within two (2) business days after learning of
            the loss, theft or unauthorized use, and we can establish that we
            could have prevented the unauthorized transfer(s) if you had told us
            in time, you could lose as much as $500.00. Also, if your periodic
            account statement shows unauthorized transfers and you DO NOT tell
            us within sixty (60) days after the statement was delivered to you,
            you may not get back any money you lose after the sixty (60) day
            period if we can prove that we could have prevented the unauthorized
            transfer(s) if you had told us in time. If an extenuating
            circumstance (such as extended travel or hospitalization) prevents
            you from promptly notifying us of a suspected lost or stolen access
            device or of any other suspected unauthorized transfer(s), the time
            periods specified in this Section 4.2 may be extended for a
            reasonable period.
          </p>
          <p>
            If your periodic account statement issued by your bank or financial
            institution shows Unauthorized Electronic Fund Transfers and you DO
            NOT tell us within sixty (60) days after the statement was delivered
            to you, you may not get back any money.
          </p>
          <p>
            Unauthorized Transfers:{' '}
            <span className="font-bold">
              Contact us immediately at{' '}
              <a className="underline" href="mailto:support@atm.com">
                support@atm.com
              </a>{' '}
              if you believe that an unauthorized EFT has occurred or may occur
              concerning your Bank Account or if your App login Credentials have
              been lost, stolen or compromised.
            </span>
          </p>
          <p>
            The liability provisions in this Section 4.2 apply to unauthorized
            transactions initiated through the ATM.com Services, including
            unauthorized access to your ATM.com Account, unauthorized Advance
            requests, and unauthorized withdrawals or transfers from your
            ATM.com Account. Liability for unauthorized transactions on your
            linked Bank Account that are not initiated through the ATM.com
            Services is governed by your bank’s or financial institution’s own
            policies and Regulation E obligations. If you believe an
            unauthorized transaction has occurred on your linked Bank Account
            that was not initiated through ATM.com, you should contact your bank
            or financial institution directly.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.3 Our Liability
          </h3>
          <p>
            We will provide you information about each transaction that debits
            or credits your linked Bank Account or linked Debit Card.
          </p>
          <p>
            We will be liable for actual damages proximately caused by our error
            or failure to complete a transaction on time or in the correct
            amount when properly instructed by You in accordance with this
            Agreement.
          </p>
          <p>
            We will not be liable for losses or damages if: the Bank Account you
            specify as the payment source is closed or does not contain
            sufficient funds to complete the payment or the charge is rejected
            or returned by your bank or financial institution; a third party’s
            app or other system breaks down or does not work properly; the
            Services were not working properly and you knew about the problems
            when you started your payment; through no fault of ours, you do not
            have enough money in your Bank Account to make a payment; we do not
            complete a transaction because we had reason to believe the
            transaction is unauthorized or illegal; we cannot complete a payment
            due to fraud or attacks on our systems or the Services;
            circumstances beyond our control (such as fire or flood) prevent a
            payment, despite reasonable precautions we have taken; and other
            possible exceptions as stated elsewhere in these Terms of Service.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.4 Documentation
          </h3>
          <p>
            A history of your ATM Instant Cash Advance deposits and repayments
            can be viewed within the ATM App by navigating to the account
            settings page and clicking on “ATM Instant Cash”. You will not
            receive printed statements listing transactions; transactions will
            appear only on your Linked Bank Account statements or in the App.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            4.5 Your Right to Stop Payment and Dispute Transactions
          </h3>
          <p>
            Under the Electronic Fund Transfer Act (15 U.S.C. § 1693 et seq.)
            and Regulation E (12 CFR Part 1005), you have the right to stop
            payment on any preauthorized electronic fund transfer from your Bank
            Account by notifying your bank or financial institution at least
            three (3) business days before the scheduled date of the transfer.
            For preauthorized debits initiated via debit card, you may also
            contact your card issuer to dispute or block the transaction. For
            preauthorized debits initiated via ACH, you may place a stop-payment
            order with your bank. You may also revoke your authorization for
            future preauthorized transfers by notifying ATM.com as described in
            Section 3 of these Terms.
          </p>
          <p>
            ATM.com will not interfere with your exercise of these rights.
            However, stopping payment on a preauthorized debit, initiating a
            chargeback, or revoking authorization does not cancel, reduce, or
            extinguish your obligation to repay any outstanding Advance or
            associated fees. If you stop a payment, initiate a chargeback, or
            revoke authorization while amounts remain outstanding, you remain
            responsible for repaying those amounts through alternative means,
            including prepayment from within the App.
          </p>
          <p>
            ATM.com may exercise any remedies available under these Terms in
            connection with unpaid amounts, including but not limited to:
            suspending or restricting your access to Services, including ATM
            Instant Cash; withholding or offsetting any positive ATM.com Account
            balance against outstanding obligations; permanently restricting
            your eligibility for future Advances; and reviewing the
            circumstances of stopped payments, chargebacks, or revoked
            authorizations as part of ATM.com’s fraud and abuse prevention
            program under Section 1.3(K).
          </p>
          <h2
            id="anti-money-laundering"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            5. ANTI-MONEY LAUNDERING / SANCTIONS / COMPLIANCE WITH LAW
          </h2>
          <p>
            ATM.com may restrict, suspend, delay, or refuse transactions or
            account access when reasonably necessary to:
          </p>
          <ul className="list-disc pl-6">
            <li>comply with anti-money laundering laws</li>
            <li>comply with sanctions programs</li>
            <li>prevent fraud</li>
            <li>respond to legal process</li>
            <li>cooperate with law enforcement</li>
            <li>comply with regulatory guidance</li>
          </ul>
          <p>
            ATM.com is not liable for delays or restrictions required by law.
          </p>
          <p>
            Nothing in these Terms limits your rights under applicable consumer
            protection statutes.
          </p>
          <h2
            id="error-resolution"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            6. ERROR RESOLUTION
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            6.1 General Error Resolution — ATM.com Account Transactions
          </h3>
          <p>
            <span className="block underline">
              A. What to Do If You Think There Is an Error
            </span>
            If you believe there has been an error involving a transaction on
            your ATM.com Account — including but not limited to an incorrect
            amount debited or credited, an unauthorized transaction, a
            transaction you did not authorize, a failure to properly complete a
            transfer, or a mathematical or computational error — contact us as
            soon as possible by emailing{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>{' '}
            or by contacting us through the App. You may also write to us at
            ATM.com, Inc., P.O. Box 7475, Newport Beach, CA 92658-7475, Attn:
            Error Resolution.
          </p>
          <p>
            ALL QUESTIONS ABOUT TRANSACTIONS MADE THROUGH ATM.COM MUST BE
            DIRECTED TO ATM.COM, AND NOT TO THE BANK OR OTHER FINANCIAL
            INSTITUTION WHERE YOU MAINTAIN YOUR LINKED BANK ACCOUNT. ATM.com is
            responsible for the Services and for resolving any errors in
            transactions made in connection with the Services.
          </p>
          <p>
            <span className="block underline">
              B. Time Limit for Reporting Errors
            </span>
            You must notify ATM.com of a suspected error no later than sixty
            (60) days after ATM.com sends or makes available to you the first
            statement, receipt, transaction history, or other written record on
            which the error or problem appeared. ATM.com strongly encourages you
            to report suspected errors as soon as you become aware of them, as
            prompt reporting enables faster investigation and resolution. If you
            fail to notify us within sixty (60) days, you may lose the ability
            to recover any funds lost after the sixty (60) day period if we can
            demonstrate that we could have prevented the loss had you notified
            us in time.
          </p>
          <p>
            <span className="block underline">
              C. Information Required When Reporting an Error
            </span>
            When contacting ATM.com to report a suspected error, please provide
            the following information:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Your full name and the login ID, email address, and phone number
              associated with your ATM.com Account
            </li>
            <li>
              A clear description of the error or the transaction you believe
              was unauthorized or incorrect, including why you believe it is an
              error
            </li>
            <li>The date of the transaction or error</li>
            <li>
              The dollar amount of the suspected error or unauthorized
              transaction
            </li>
            <li>
              Any additional information that may help us identify and
              investigate the error
            </li>
          </ul>
          <p>
            You may be asked to provide your report in writing. If we request a
            written confirmation and you do not provide it within ten (10)
            business days of our request, we may not be obligated to
            provisionally credit your Account as described in below.
          </p>
          <p>
            <span className="block underline">
              D. ATM.com's Investigation and Resolution Timeline
            </span>
            Upon receiving your error report, ATM.com will investigate promptly.
            The following timelines apply:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Standard Investigation Period. We will determine whether an error
              occurred within ten (10) business days of receiving your complete
              error report and will correct any error promptly. During this
              period, we will have full use of the funds in question.
            </li>
            <li>
              Extended Investigation — Provisional Credit. If we need additional
              time to investigate — up to forty-five (45) days from receipt of
              your error report for standard transactions — we will
              provisionally credit your ATM.com Account for the amount you
              believe is in error within ten (10) business days of receiving
              your report, so that you will have use of the funds while the
              investigation is pending. We will notify you of the provisional
              credit when it is applied.
            </li>
            <li>
              New Account Transactions. If the error involves a transaction on
              an ATM.com Account that has been open for thirty (30) days or
              less, the investigation period may be extended to twenty (20)
              business days (instead of ten) before provisional credit is
              required, and the extended investigation period may be up to
              ninety (90) days (instead of forty-five).
            </li>
            <li>
              Point-of-Sale and Foreign-Initiated Transactions. If the error
              involves a point-of-sale transaction or a transaction initiated
              outside the United States, the extended investigation period
              described in Section 6.1(D) may be up to ninety (90) days from
              receipt of your error report, rather than forty-five (45) days.
            </li>
            <li>
              Notice of Results. We will notify you of the results of our
              investigation within three (3) business days after completing it.
              If we determine that an error did occur, we will correct it
              promptly, including crediting any applicable interest and
              reversing any related fees where required. If we determine that no
              error occurred, we will provide you with a written explanation
              and, if applicable, reverse any provisional credit previously
              applied. You may request copies of the documents we relied upon in
              making our determination.
            </li>
            <li>
              Reversal of Provisional Credit. If we provisionally credited your
              Account and subsequently determine that no error occurred, we will
              reverse the provisional credit. We will notify you at least five
              (5) business days before reversing any provisional credit so that
              you have an opportunity to withdraw the funds.
            </li>
          </ul>
          <p>
            <span className="block underline">
              E. Limitations on ATM.com's Liability for Unauthorized Transfers
            </span>
            If you notify ATM.com within two (2) business days after you learn
            of the loss or theft of your Credentials or mobile device, ATM.com's
            liability for unauthorized transfers shall not exceed fifty dollars
            ($50.00).
          </p>
          <p>
            If you do not notify ATM.com within two (2) business days after you
            learn of the loss or theft of your Credentials or mobile device, and
            ATM.com can demonstrate that it could have prevented the
            unauthorized transfers had you notified us in time, ATM.com's
            liability for unauthorized transfers that occur after the
            two-business-day period shall not exceed five hundred dollars
            ($500.00).
          </p>
          <p>
            If you fail to report an unauthorized transfer that appears on a
            statement or transaction history within sixty (60) days of the date
            that statement or record was sent or made available to you, and
            ATM.com can demonstrate that it could have prevented the loss had
            you reported it in time, you may be liable for the full amount of
            the unauthorized transfers occurring after the sixty (60) day
            period.
          </p>
          <p>
            These liability limitations are provided in accordance with the
            Electronic Fund Transfer Act, 15 U.S.C. § 1693 et seq., and
            Regulation E, 12 C.F.R. Part 1005. Nothing in this Section 6.1(E)
            shall be construed to limit ATM.com's liability beyond what
            applicable law permits or to expand your liability beyond what
            applicable law allows.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            6.2 Error Resolution for ATM Instant Cash
          </h3>
          <p>
            <span className="block underline">A. Scope</span>
            This Section 6.2 applies specifically to errors involving ATM
            Instant Cash transactions, including errors in the amount of an
            Advance disbursed, unauthorized Advance requests, incorrect
            repayment debits, and any other transaction error arising from or
            relating to ATM Instant Cash. For all other Account transaction
            errors, Section 6.1 applies.
          </p>
          <p>
            <span className="block underline">
              B. What to Do If You Think There Is an Error
            </span>
            If you believe there has been an error involving an ATM Instant Cash
            transaction, contact us as soon as possible by emailing{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            . Please do not contact your linked bank or financial institution to
            dispute an ATM Instant Cash transaction — ATM.com is solely
            responsible for resolving errors arising from ATM Instant Cash
            transactions.
          </p>
          <p>
            <span className="block underline">
              C. Time Limit for Reporting Errors
            </span>
            You must notify ATM.com of a suspected ATM Instant Cash error no
            later than sixty (60) days after ATM.com sends or makes available to
            you the first statement, receipt, or written record reflecting the
            transaction in question. Prompt reporting is strongly encouraged.
          </p>
          <p>
            <span className="block underline">
              D. Information Required When Reporting an Error
            </span>
            When reporting a suspected ATM Instant Cash error, please provide:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Your full name and the email address and phone number associated
              with your ATM.com Account
            </li>
            <li>
              A clear and detailed description of the transaction you believe is
              in error and why you believe it is an error
            </li>
            <li>The date and amount of the transaction in question</li>
            <li>
              Any supporting documentation you have, such as screenshots of
              transaction confirmations or bank statements
            </li>
          </ul>
          <p>
            <span className="block underline">
              E. Investigation and Resolution Timeline
            </span>
            The same investigation and resolution timelines set forth in Section
            6.1(D) apply to ATM Instant Cash error investigations, including the
            standard ten (10) business day investigation period, the extended
            forty-five (45) day investigation period with provisional credit,
            the new account timelines, and the point-of-sale and
            foreign-initiated transaction timelines. ATM.com will notify you of
            the results of any ATM Instant Cash error investigation within three
            (3) business days after completing it.
          </p>
          <p>
            <span className="block underline">F. Repayment Disputes</span>
            If you believe that ATM.com incorrectly debited your linked Bank
            Account for repayment of an ATM Instant Cash Advance — including but
            not limited to a debit in the wrong amount, a debit taken on the
            wrong date, or a duplicate debit — you must report the error in
            accordance with Section 6.2 within the sixty (60) day period
            described in Section 6.2(C). ATM.com will investigate and, if an
            error is confirmed, will promptly credit your ATM.com Account or
            your linked Bank Account for the erroneous amount, as appropriate.
          </p>
          <p>
            You agree that you will not attempt to resolve repayment disputes by
            initiating a chargeback or dispute directly with your bank or debit
            card issuer. Initiating a chargeback in lieu of using ATM.com's
            error resolution process may be treated as a violation of these
            Terms as described in Section 8.7.
          </p>
          <p>
            <span className="block underline">
              G. CFPB and Regulatory Complaints
            </span>
            Nothing in this Section 6.2 limits your right to file a complaint
            with the Consumer Financial Protection Bureau ("CFPB") or any other
            applicable state or federal regulatory authority. You may contact
            the CFPB at www.consumerfinance.gov/complaint or by calling
            1-855-411-CFPB (2372). Filing a regulatory complaint does not toll
            the sixty (60) day error reporting period described in this Section
            6.2, so you should contact ATM.com directly as soon as you become
            aware of a suspected error while simultaneously pursuing any
            regulatory complaint you wish to file.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            6.3 Partner Offer Missing Credit Resolution
          </h3>
          <p>
            If you have completed an offer that has not registered as completed
            in the App, you must contact us by sending an email to{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>{' '}
            as soon as possible. To be eligible for investigation and
            resolution, we must receive your support request within sixty (60)
            days of the date the offer was completed (the "Offer Credit Claim
            Period"). Completed offers that have not registered in the App will
            not be eligible for investigation or credit if we do not receive a
            timely support request before the end of the Offer Credit Claim
            Period.
          </p>
          <p>
            Notwithstanding the foregoing, if you have initiated the informal
            dispute resolution process described in Section 22.2 of these Terms
            within the Offer Credit Claim Period, the Offer Credit Claim Period
            shall be tolled — meaning it shall stop running — for the duration
            of the informal dispute resolution period described in Section 22.2,
            and shall resume running only upon the conclusion of that process.
            This tolling provision ensures that your good-faith participation in
            the informal dispute resolution process will not cause you to
            forfeit your right to seek an offer credit under this Section 6.3.
          </p>
          <p>
            For the avoidance of doubt, the Offer Credit Claim Period is a
            separate and independent deadline from any statute of limitations or
            filing deadline applicable to formal legal claims. Nothing in this
            Section 6.3 shall be construed to shorten or otherwise affect any
            statute of limitations period applicable to claims that are subject
            to the arbitration provisions of Section 22, which are governed by
            Section 22.2's tolling provisions.
          </p>
          <p>
            If your support request is received within the Offer Credit Claim
            Period, ATM.com will investigate the matter and, if the offer
            completion is verified, will apply the appropriate credit to your
            ATM.com Account. ATM.com does not guarantee that all missing offer
            credits can be verified or recovered, as our ability to do so
            depends in part on reporting and tracking data received from
            third-party networks and advertisers, which may not always be
            available or conclusive.
          </p>
          <h2
            id="third-party-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            7. COLLECTION AND USE OF THIRD-PARTY FINANCIAL INFORMATION
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.1 General Authorization
          </h3>
          <p>
            By using the Services, including ATM Instant Cash, you consent to
            and authorize ATM.com to access and collect your account transaction
            information maintained by non-affiliated third parties including,
            but not limited to, balance information, transaction dates, merchant
            names, and transaction types.
          </p>
          <p>
            By using the Services, you grant ATM.com and our third-party service
            providers the right, power, and authority to act on your behalf to
            access and transmit your personal and financial information from
            your relevant financial institutions. All data is transmitted and
            stored in compliance with our Privacy Policy and the privacy
            policies of the applicable third-party service providers. You have
            the right to opt out of this data collection at any time by
            contacting us at{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            , provided that opting out may limit or eliminate your ability to
            use certain features of the Services, including ATM Instant Cash.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.2 Cardlytics Partnership and Bank Transaction Data
          </h3>
          <p>
            ATM.com partners with Cardlytics, Inc. ("CDLX") to provide earning
            opportunities and Customer Incentives to you based on your banking
            transaction data. In connection with this partnership, your banking
            transaction data — which may include, but is not limited to,
            transaction amounts, merchant names, transaction dates, and
            transaction categories — will be accessed, processed, and used by
            CDLX to identify and deliver relevant earning opportunities and
            Customer Incentives to you through the Services. This data is
            provided to CDLX in part through Plaid, Inc. ("Plaid"), ATM.com's
            bank account connectivity provider.
          </p>
          <p className="font-bold">
            BY AGREEING TO THESE TERMS, YOU ARE EXPRESSLY CONSENTING TO THE
            FOLLOWING, EACH OF WHICH IS DESCRIBED IN MORE DETAIL BELOW:
          </p>
          <ul className="list-disc pl-6">
            <li>
              ATM.com and CDLX may access, use, and process your banking
              transaction data provided through Plaid for the purposes described
              in this Section 7.2
            </li>
            <li>
              Your banking transaction data may be shared with and/or sold to
              third parties by CDLX in accordance with CDLX's own privacy
              policy, a current copy of which is available at
              [www.cardlytics.com/privacy-policy]. ATM.com does not control
              CDLX's data sharing or sale practices, and you should review
              CDLX's privacy policy carefully before agreeing to participate in
              the Customer Incentive program
            </li>
          </ul>
          <p>
            You acknowledge that the sharing and/or sale of your banking
            transaction data to third parties as described above may constitute
            a "sale" or "sharing" of personal information under the California
            Consumer Privacy Act, as amended by the California Privacy Rights
            Act (collectively, "CCPA"). Your rights with respect to the sale or
            sharing of your personal information, including your right to opt
            out, are described in Section 27 of these Terms and in our Privacy
            Policy. To exercise your right to opt out of the sale or sharing of
            your personal information, please visit [[Do Not Sell or Share My
            Personal Information]] or contact us at{' '}
            <a className="underline" href="mailto:privacy@atm.com">
              privacy@atm.com
            </a>
            .
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.3 How to Opt Out of the CDLX Program
          </h3>
          <p>
            Your participation in the CDLX Customer Incentive program is
            voluntary. You may withdraw your consent for the sharing of your
            banking transaction data with CDLX at any time directly within the
            App. Upon withdrawal of your consent, ATM.com will cease sharing
            your banking transaction data with CDLX on a going-forward basis;
            however, data that has already been shared with or processed by CDLX
            prior to your opt-out may remain subject to CDLX's data retention
            and use policies, over which ATM.com has no control. Withdrawing
            your consent will remove you from the CDLX Customer Incentive
            program, and you will no longer receive Customer Incentives based on
            your banking transaction data.
          </p>
          <p>
            Opting out of the CDLX program does not constitute a withdrawal of
            your general consent to ATM.com's collection and use of your
            financial data as described in Section 7.1 above, nor does it affect
            ATM.com's ability to provide the core Services to you.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.4 Plaid Terms and Data Use
          </h3>
          <p>
            ATM.com uses Plaid, Inc. to establish and maintain secure
            connections between your Bank Account and ATM.com. By using the
            Services and linking your Bank Account, you agree to Plaid's Terms
            of Use and Privacy Policy, current versions of which are available
            at plaid.com/legal. You acknowledge that Plaid's collection, use,
            and sharing of your information is governed by Plaid's own privacy
            policy and is separate from ATM.com's Privacy Policy. ATM.com is not
            responsible for Plaid's data practices.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.5 Categories of Third Parties with Whom Financial Data May Be
            Shared
          </h3>
          <p>
            In connection with the Services, your financial and banking
            transaction data may be shared with or accessed by the following
            categories of third parties:
          </p>
          <ul className="list-disc pl-6">
            <li>Bank account connectivity providers (currently Plaid, Inc.)</li>
            <li>
              Advertising and marketing analytics partners (currently including
              CDLX)
            </li>
            <li>Fraud detection and identity verification service providers</li>
            <li>Payment processing providers</li>
            <li>
              Any successor or replacement service providers performing
              substantially similar functions
            </li>
          </ul>
          <p>
            ATM.com will update this Section 7.5 if the categories of third
            parties with whom your financial data is shared materially change.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            7.6 Your CCPA Rights Regarding Financial Data
          </h3>
          <p>
            If you are a California resident, you have specific rights under the
            CCPA with respect to your personal information, including your
            banking transaction data. These rights include the right to know
            what personal information is collected and how it is used, the right
            to delete your personal information, the right to correct inaccurate
            personal information, the right to opt out of the sale or sharing of
            your personal information, and the right to non-discrimination for
            exercising your privacy rights. A full description of your CCPA
            rights and how to exercise them is set forth in Section 27 of these
            Terms and in our Privacy Policy. To submit a privacy rights request,
            contact us at{' '}
            <a className="underline" href="mailto:privacy@atm.com">
              privacy@atm.com
            </a>{' '}
            .
          </p>
          <h2
            id="age-restrictions"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            8. ELIGIBILITY AND AGE RESTRICTIONS; YOUR ACCOUNT AND PROHIBITED
            CONDUCT
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.1 Eligibility and Age Restrictions
          </h3>
          <p>
            To register and/or use the Services you must be a minimum of 18
            years of age and be a legal resident of, and physically located
            within, the Service Area. Use by persons under the age of 18 is
            strictly prohibited. By registering and/or using the Services, you
            represent and warrant that you are at least 18 years old and that
            you have the right, authority and capacity to enter into and abide
            by the terms and conditions of this Agreement. You may not allow
            other persons to use your ATM.com Account, and you agree that you
            are the sole authorized user of your ATM.com Account.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.2 Authorization to Retrieve and Use Information You Provide
          </h3>
          <p>
            By providing information about yourself, including your Credentials
            and other content through the Services (“Data Retrieval
            Information"), you are licensing such Data Retrieval Information to
            ATM.com for the purpose of providing the Services. You represent
            that you are entitled to provide the Data Retrieval Information to
            ATM.com to use for the purpose of providing the Services, without
            any obligation by ATM.com to pay any fees or be subject to any
            restrictions or limitations. You hereby authorize and permit ATM.com
            to use and store the Data Retrieval Information for the purpose of
            providing the Services, including configuring the Services to be
            compatible with third-party websites that enable ATM.com to provide
            the Services. ATM.com will store Data Retrieval information in
            encrypted form.
          </p>
          <p>
            As part of these Terms and Agreement, and solely for the purpose of
            providing the Services, you grant ATM.com a limited power of
            attorney, and appoint ATM.com as your attorney-in-fact and agent, to
            access third-party websites, use Data Retrieval Information you
            provide, and retrieve Account Information, with the full power and
            authority to do and perform each task necessary in connection with
            such activities as you could do in person.
          </p>
          <p>
            YOU ACKNOWLEDGE AND AGREE THAT WHEN ATM.COM IS ACCESSING AND
            RETRIEVING ACCOUNT INFORMATION FROM THIRD-PARTY WEBSITES, ATM.COM IS
            ACTING AS YOUR AGENT, AND NOT AS THE AGENT OF OR ON BEHALF OF THE
            THIRD PARTY THAT OPERATES THE THIRD-PARTY WEBSITE.{' '}
          </p>
          <p>
            ATM.com cannot always foresee or anticipate technical or other
            difficulties that may result in failure to obtain data or in loss of
            data. Further, ATM.com does not assume responsibility for the
            timeliness, accuracy, deletion, or non-delivery of, or failure to
            store, any data.
          </p>
          <p>
            You represent and warrant to us that you have all rights to the Data
            Retrieval Information and Account Information that you provide to us
            via the Services. You are responsible for the accuracy and
            completeness of the Data Retrieval Information and Account
            Information you provide through the Services. You, and not ATM.com,
            are liable in the event that such Data Retrieval Information and/or
            Account Information is not accurate or complete. You agree that you
            will indemnify, defend, and hold harmless ATM.com for all claims by
            third parties resulting from Data Retrieval Information and/or
            Account Information that you provide us. We reserve the right, at
            our own expense, to assume the exclusive defense and control of such
            disputes, and in any event, you will cooperate with us in asserting
            any available defenses.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.3 Account Ownership and Responsibilities
          </h3>
          <p>
            Subject to your compliance with these Terms and Agreement, we grant
            you a limited, non-exclusive, non-sublicensable, non-transferable,
            and revocable right to access and use the Services only for your own
            internal, personal, or non-commercial use, and only in a manner that
            complies with all local, state, national, and foreign laws,
            treaties, regulations, and other legal requirements that apply to
            you or your use of the Services, including those relating to data
            security and data privacy. If your use of the Services is prohibited
            by applicable law, then you do not have authorization to use the
            Services. ATM.com is not responsible for unlawful uses of the
            ATM.com App or Services.
          </p>
          <p>
            In addition to the other requirements listed throughout these Terms,
            in order to use the Services, you: (a) must register with us on the
            ATM.com Application or Website; (b) must be located in the United
            States of America (including District of Columbia but excluding
            Puerto Rico, US Virgin Islands, and other United States territories
            and possessions) (c) have a Bank Account with a U.S. financial
            institution; and (d) provide all information requested by us, such
            as your name, email address, mobile device number, Data Retrieval
            Information and Account Information, and such other information as
            we may request from time to time (collectively, "User Information").
          </p>
          <p>
            Each ATM.com user may establish, maintain, use and control only one
            ATM.com Account. For avoidance of doubt, users may not "co-own" or
            “co-operate” ATM.com Accounts. Only one unique ATM.com account may
            be concurrently associated with one unique device at any given time.
            Only one unique device may be concurrently associated with one
            unique ATM.com account at any given time.
          </p>
          <p>
            You may only have one Bank Account linked to your ATM.com account at
            any given time. Once that Bank Account is linked to your ATM.com
            account, that Bank Account is indefinitely tied to that account
            only. In other words, it can never be linked to any other ATM.com
            account. You may unlink a Bank Account to your ATM.com Account and
            replace it with another Bank Account but you may only link two Bank
            Accounts over the lifetime of your ATM.com account. We reserve the
            right to waive or suspend this limitation in special situations.
          </p>
          <p>
            In the event ATM.com determines that a user has established, owned,
            maintained, used or controlled more than one ATM.com Account, or
            more than one user has used an ATM.com Account, in addition to any
            other rights defined by ATM.com, ATM.com reserves the right to
            suspend or terminate any or all such ATM.com Accounts and withhold
            or revoke any money earned through the Application associated with
            those accounts.
          </p>
          <p>
            You are solely responsible for any and all activity that occurs
            through your Account and you agree not to sell, transfer, license or
            assign your Account or any Account rights. ATM.com strictly
            prohibits the creation of accounts for anyone other than yourself.
          </p>
          <p>
            Additionally, you represent that all information you will provide or
            have provided to ATM.com upon registration and at all other times
            will be true, accurate, current and complete and you agree to update
            your User Information as necessary to maintain its validity.
          </p>
          <p>
            You must keep your User Information current with us. In order to
            ensure that we are able to provide all records, notices, statements,
            communications, and other items for all services provided to you
            under these Terms and in connection with your relationship with us,
            to you electronically, you must notify us of any change in your
            email address and your mobile device number or other text message
            address by updating your profile on the App or Website.
          </p>
          <p>
            Use of the Services may be available through a compatible mobile
            device with internet access and require certain software. You agree
            that you are solely responsible for these requirements, including
            any applicable changes, updates, and fees associated with them, as
            well as for complying with the terms of your mobile device and
            telecommunications provider.
          </p>
          <p>
            ATM.com reserves the right to block a user from accessing all or
            certain of the Services as a result of non-compliance with the terms
            or suspicious user behavior.
          </p>
          <p>
            It is your responsibility to keep your login ID and password secure
            and confidential. IF YOUR CREDENTIALS INCLUDING APP LOGIN ID OR
            PASSWORD ARE LOST OR STOLEN, NOTIFY US AT ONCE by emailing{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            .
          </p>
          <p>
            ATM.COM MAKES NO WARRANTIES OR REPRESENTATIONS OF ANY KIND, EXPRESS,
            STATUTORY, OR IMPLIED AS TO (A) THE AVAILABILITY OF
            TELECOMMUNICATION SERVICES FROM YOUR PROVIDER OR ACCESS TO THE
            SERVICES AT ANY TIME OR FROM ANY LOCATION; (B) ANY LOSS, DAMAGE, OR
            SECURITY INTRUSION OF THE TELECOMMUNICATION SERVICES; AND (C) ANY
            DISCLOSURE OF INFORMATION TO THIRD PARTIES OR FAILURE TO TRANSMIT
            ANY DATA, COMMUNICATIONS, OR SETTINGS IN CONNECTION WITH THE
            SERVICES.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.4 Account Status and Maintenance Fees
          </h3>
          <p>
            <span className="block underline">
              A. Active and Inactive Accounts
            </span>
            An "active" ATM.com Account is one that has accessed the Services
            within the last 45 days. Accessing the Services includes simply
            logging into the account in the App and/or utilizing any functions
            of the App. An "inactive" ATM.com Account is one that has not
            accessed the Services for a consecutive period of 45 days.
          </p>
          <p>
            <span className="block underline">B. Maintenance Fee</span>
            To sustain an inactive account, ATM.com reserves the right to deduct
            a monthly maintenance fee of $3.00 from your ATM.com Account balance
            for each month the account remains inactive. The maintenance fee
            will not be assessed if your ATM.com Account balance is less than
            $3.00 at the time the fee would otherwise be applied — in other
            words, ATM.com will never charge a maintenance fee that exceeds your
            available account balance or that would cause your balance to go
            negative.
          </p>
          <p>
            Abandoned Accounts. Accounts that have been inactive for more than
            180 days may be considered "abandoned" and may be suspended or
            terminated at ATM.com's discretion. ATM.com will make reasonable
            efforts to notify you by email before suspending or terminating an
            abandoned account.
          </p>
          <p>
            <span className="block underline">C. Return of Funds</span>
            Escheatment. ATM.com will attempt to return any remaining balance in
            an abandoned account to your linked Bank Account. It is your
            responsibility to ensure your Bank Account is active and able to
            accept deposits. If ATM.com is unable to return your funds, those
            funds may be surrendered to the appropriate state governmental
            authority as required by applicable unclaimed property law. Each
            state has its own laws governing when an account balance is subject
            to escheatment. If required by applicable law, ATM.com will make
            reasonable efforts to contact you before transferring your balance
            to the state. After ATM.com surrenders funds to the applicable state
            authority, ATM.com has no further liability to you for those funds
            and you must apply directly to the appropriate state agency to
            reclaim them.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.5 Account Funds
          </h3>
          <p>
            All monies earned via the Services provided by ATM.com are recorded
            as a liability of ATM.com to the user until said funds are claimed
            via an active ATM.com Account with an approved Bank Account that has
            reached the Minimum Account Balance. An active ATM.com Account is
            defined in Section 8.4 above. ATM.com uses Plaid{' '}
            <a className="underline" href="plaid.com" target="_blank">
              (plaid.com)
            </a>{' '}
            to connect users’ Bank Accounts to ATM.com. A user must connect a
            Bank Account that is supported by Plaid to form a secure connection
            with ATM.com. This is to ensure that funds can be securely
            transferred directly to a user’s Bank Account. Any Bank Account that
            is not supported by Plaid’s authentication services will not be
            accepted by ATM.com at this time, although exceptions may be made at
            the sole discretion of ATM.com. ATM.com reserves the right to block
            certain accounts that are offered by Plaid – PayPal, as an example.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.6 Prohibited Conduct
          </h3>
          <p>
            You may not use the funds in your ATM.com Account to make purchases,
            withdraw cash, transfer funds to non-ATM.com affiliated third
            parties, or for any other purpose. We are not responsible for any
            third-party fees that may be incurred as a result of using the
            Services, including, but not limited to, third-party fees incurred
            as a result of maintaining insufficient funds in your Bank Account.
          </p>
          <p>
            You agree not to authorize any other person or entity to use your
            Credentials or mobile device to access the Services. You are solely
            responsible for the maintenance, confidentiality, and security of
            your Credentials, and other User information. Except as otherwise
            required by applicable law, you are responsible for all transactions
            and other activities authorized or performed using your Credentials
            or mobile device, whether authorized or unauthorized by you. Except
            as otherwise expressly stated in these Terms or required by
            applicable law, we are not responsible for any losses arising out of
            the loss or theft of your User Information or your mobile device or
            from unauthorized or fraudulent transactions associated with your
            Bank Account or your ATM.com Account. If you suspect or become aware
            of any unauthorized activity or access to your Credentials or mobile
            device, you must contact us immediately at{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            .
          </p>
          <p>
            The Services may permit you to submit content, send emails and other
            communications, and provide other information for publication or
            distribution to third parties (collectively, "User Content").
          </p>
          <p>
            You may not post any prohibited content including, but not limited
            to, pictures, videos, comments, or links of violent, discriminatory,
            unlawful, infringing, hateful, sexually explicit or pornographic
            content via the Services.
          </p>
          <p>
            You may not defame, stalk, bully, abuse, harass, threaten,
            impersonate or intimidate people or entities, and you must not post
            private or confidential information in User Content via the
            Services, including, without limitation, your or any other person's
            credit card information, social security or other personal
            identification, non-public phone numbers or email addresses.
          </p>
          <p>
            You may not use the Services for any illegal or unauthorized
            purpose. You agree to comply with all laws, rules and regulations
            (i.e. federal, state, local and provincial) applicable to your use
            of the Services and your User Content, including but not limited to,
            copyright laws.
          </p>
          <p>
            You may not change, modify, adapt or alter the Services or change,
            modify or alter another website so as to falsely imply that it is
            associated with the Services or ATM.com.
          </p>
          <p>
            You agree that you will not solicit, collect or otherwise use the
            login credentials of other ATM.com users.
          </p>
          <p>
            You must not interfere or disrupt the Services or servers or
            networks connected to the Services, including by transmitting any
            worms, viruses, spyware, malware or any other code of a destructive
            or disruptive nature. You may not inject content or code or
            otherwise alter or interfere with the way any ATM.com page is
            rendered or displayed in a user's browser or device.
          </p>
          <p>
            You understand and agree that ATM.com cannot and will not be
            responsible for the Content posted on the Services and you use the
            Services at your own risk. If you violate the letter or spirit of
            these Terms, or otherwise create risk or possible legal exposure for
            ATM.com, we can stop providing all or part of the Services to you
            without warning or notice.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.7 Chargebacks
          </h3>
          <p>
            ATM.com reserves the right to freeze or close accounts that have
            funds that have been charged back. A chargeback is a forced
            transaction reversal initiated by a bank. Chargebacks occur when a
            bank settles a debt due to loss on a fraudulent or disputed
            transaction.
          </p>
          <p>
            ATM.com is not responsible for any third-party fees or losses that
            may be incurred as a result of using the Services, including, but
            not limited to, third-party fees incurred as a result of maintaining
            insufficient funds in your Bank Account. Please consult with your
            bank regarding their chargeback policies.
          </p>
          <p>
            Attempts to access funds that have been charged back may be
            suspended if ATM.com deems these transfers to be illegitimate or
            fraudulent. ATM.com may investigate patterns of chargebacks, stopped
            payments, or disputed debit card transactions as part of its fraud
            and abuse prevention program under Section 1.3(K). If ATM.com
            determines, in its reasonable judgment, that a chargeback, stop
            payment, or dispute was initiated with the intent to obtain an
            Advance without repayment or otherwise constitutes fraud, abuse, or
            evasion of repayment obligations, ATM.com may suspend or terminate
            your account and restrict your access to all Services. Nothing in
            this Section 8.7 limits your rights under the Electronic Fund
            Transfer Act, including your right to stop payment on a
            preauthorized electronic fund transfer under 12 CFR § 1005.10(c), to
            dispute a debit card transaction with your card issuer, or to assert
            errors under ATM.com’s error resolution process in Section 6.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            8.8 User Account Termination
          </h3>
          <p>
            We reserve the right to modify or terminate the Services or your
            access to the Services, including ATM Instant Cash, for any reason,
            without notice, at any time, and without liability to you. You can
            deactivate your ATM.com Account by contacting us at{' '}
            <Link className="underline" href=" https://www.ATM.com/contact">
              https://www.ATM.com/contact
            </Link>{' '}
            or emailing{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>{' '}
            or closing your Account from within the App. If we terminate your
            access to the Services or you use the form detailed above to
            deactivate your account, your account history will no longer be
            accessible through your account.
          </p>
          <p>
            ATM.com reserves the right to suspend or terminate access to
            Services in connection with its fraud and abuse prevention program
            under Section 1.3(K), which may include review of patterns of
            disputed transactions, chargebacks, or stopped payments. If ATM.com
            determines, following review, that a dispute, chargeback, or stop
            payment was initiated with the intent to evade repayment or
            otherwise constitutes fraud or abuse, ATM.com may cancel your
            account and all Services.
          </p>
          <p>
            Upon termination, all licenses and other rights granted to you in
            these Terms will immediately cease. Users with terminated accounts
            will not be eligible to create additional accounts, but may, based
            on internal review, reinstate a previously terminated account. If a
            user is found with multiple accounts, access to all accounts created
            are subject to immediate termination.
          </p>
          <h2
            id="electronic-signatures"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            9. CONSENT TO USE ELECTRONIC SIGNATURES AND ELECTRONIC
            COMMUNICATIONS
          </h2>
          <p>
            “Communications” means any and all agreements, communications,
            disclosures, notices, records, documents and/or other items that we
            provide to you, or that you consent or agree to at our request.
          </p>
          <p>
            “Electronic Communications” means any and all Communications that we
            provide to you in electronic form, and/or any and all Communications
            that you consent or agree to in electronic form, including but not
            limited to by means of your Electronic Signature.
          </p>
          <p>
            “Electronic Media” means the internet, a website, email, messaging
            services (including SMS and/or any other form of text messaging),
            software and/or applications (including applications for mobile
            devices, hand-held devices and/or other devices), and/or any other
            form of electronic media, whether existing now and/or that may exist
            in the future.
          </p>
          <p>
            “Electronic Signature” means any means that is used to indicate Your
            consent and/or agreement by means of any Electronic Media, which may
            include, but is not limited to, clicking a button or checking a box
            or taking any other action to indicate Your consent and/or agreement
            on or in any Electronic Media.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.1 Electronic Signatures and Electronic Communications
          </h3>
          <p>
            <span className="block underline">
              A. Your Right to Receive Paper Copies and to Withdraw Consent
            </span>
            Before providing your consent to electronic communications below,
            please be aware of the following important rights:
          </p>
          <ul className="list-disc pl-6">
            <li>
              You have the right to receive any Communication in paper form. If
              at any time you wish to receive a paper copy of any Electronic
              Communication that we have provided or will provide to you, you
              may request one by contacting us at{' '}
              <a className="underline" href="mailto:support@atm.com">
                support@atm.com
              </a>{' '}
              or at ATM.com, Inc., P.O. Box 7475, Newport Beach, CA 92658-7475,
              Attn: Paper Copy Request. ATM.com reserves the right to charge a
              reasonable fee for paper copies, and will notify you of any such
              fee before processing your request.
            </li>
            <li>
              You have the right to withdraw your consent to electronic
              communications. You may withdraw your consent at any time in
              accordance with Section 9.4 of these Terms. Please be aware that
              withdrawing your consent will affect your ability to use the
              Services, as described in Section 9.4.
            </li>
            <li>
              Your consent is not a condition of purchasing any goods or
              services from ATM.com beyond what is necessary to provide the
              electronic-based Services described herein. However, because the
              Services are delivered electronically, withdrawal of consent to
              electronic communications will result in termination of your
              access to the Services as described in Section 9.4.
            </li>
          </ul>
          <p>
            After reading the above, if you wish to withdraw your consent or
            request paper copies at any time, please contact us at{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>{' '}
            before or after providing your consent below.
          </p>
          <p>
            <span className="block underline">B. Scope of Your Consent</span>
            Subject to applicable law and the rights described in Section 9.1(A)
            above, by agreeing to these Terms you consent and agree to all of
            the following:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Electronic Signatures. You consent to use Electronic Signatures to
              indicate that you have read, understood, and agreed to these
              Terms, our Privacy Policy, and any other conditions or agreements
              that we may provide on any form of Electronic Media. Your
              Electronic Signature has the same legal effect as a handwritten
              signature to the fullest extent permitted by the Electronic
              Signatures in Global and National Commerce Act (E-SIGN Act), 15
              U.S.C. § 7001 et seq., and any applicable state electronic
              signature law.
            </li>
            <li>
              Receipt of Electronic Communications. You consent to receive any
              and all Communications from ATM.com in electronic form, including
              Communications that we may otherwise be required to send or
              provide to you in paper form under applicable law, to the extent
              permitted by law. Electronic Communications will be delivered to
              you by one or more of the following methods: (a) to the email
              address associated with your ATM.com Account; (b) by SMS or text
              message to the mobile phone number associated with your ATM.com
              Account; (c) by push notification through the App; or (d) by
              posting on the ATM.com website or within the App, with notice to
              you that the Communication has been posted.
            </li>
            <li>
              Deemed Receipt. Each Electronic Communication will be considered
              received by you: (a) if sent by email or SMS, at the time ATM.com
              first transmits it to the email address or mobile number
              associated with your Account, regardless of whether you actually
              open or read the Communication; (b) if delivered by push
              notification, at the time the notification is transmitted to your
              device; or (c) if posted on the website or App, within twenty-four
              (24) hours after the time it is posted. It is your responsibility
              to ensure that your email address and mobile phone number on file
              with ATM.com are current and accurate. ATM.com is not responsible
              for Electronic Communications that are not received by you due to
              an outdated or inaccurate email address or phone number in your
              Account, or due to spam filtering or other technical issues
              outside ATM.com's control.
            </li>
            <li>
              Retention of Electronic Communications. You agree to print, save,
              and/or electronically store a copy of all Electronic
              Communications that we send to you. ATM.com is not responsible for
              your failure to retain copies of Electronic Communications.
            </li>
            <li>
              Notification of Contact Information Changes. You agree to promptly
              notify ATM.com of any change to your email address, mobile device
              number, or other contact information by updating your profile in
              the App or on the Website. Failure to maintain current contact
              information may result in your failure to receive important
              Communications, for which ATM.com shall not be liable.
            </li>
            <li>
              Duration of Consent. The consents and agreements set forth in this
              Section 9.1(B) will remain in full force and effect unless and
              until you withdraw them in accordance with Section 9.4of these
              Terms.
            </li>
          </ul>
          <p>
            <span className="block underline">
              C. Specific Categories of Electronic Communications
            </span>
            The Electronic Communications to which your consent applies include,
            but are not limited to, the following categories:
          </p>
          <ul className="list-disc pl-6">
            <li>
              These Terms of Service and any amendments or modifications thereto
            </li>
            <li>
              Our Privacy Policy and any amendments or modifications thereto
            </li>
            <li>Account opening disclosures and agreements</li>
            <li>
              ATM Instant Cash agreements, disclosures, and transaction
              confirmations
            </li>
            <li>
              Account statements, transaction receipts, and payment histories
            </li>
            <li>
              Error resolution notices and responses under Section 6 of these
              Terms
            </li>
            <li>Tax documents, including any applicable 1099 forms</li>
            <li>Notices of material changes to the Services or these Terms</li>
            <li>
              Legal notices, including notices of arbitration demands or
              responses
            </li>
            <li>
              Marketing and promotional communications (subject to your right to
              opt out of marketing communications at any time without affecting
              your receipt of transactional or legal communications)
            </li>
            <li>
              Any other Communication required or permitted to be provided to
              you in connection with the Services
            </li>
          </ul>
          <p>
            <span className="block underline">
              D. Limitations on Electronic Delivery
            </span>
            Notwithstanding your consent to electronic communications, ATM.com
            acknowledges that certain Communications may be required by
            applicable law to be delivered in a specific manner that this
            Section 9.1 cannot override. To the extent any applicable federal or
            state law requires a particular Communication to be delivered in
            paper form or by a method other than electronic delivery, and such
            requirement cannot be waived by your consent herein, ATM.com will
            deliver that Communication in the manner required by law. Your
            consent in this Section 9.1 is given to the fullest extent permitted
            by the E-SIGN Act and applicable state law, and no further.
          </p>
          <p>
            <span className="block underline">E. Acknowledgment</span>
            By agreeing to these Terms, you represent and warrant to ATM.com
            that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              You have read and understood this Section 9.1in its entirety,
              including the disclosure of your right to receive paper copies and
              to withdraw consent set forth in Section 9.1(A)
            </li>
            <li>
              You have the hardware and software described in Section 9.2
              necessary to access and retain Electronic Communications
            </li>
            <li>
              Your consent to electronic communications is knowing, voluntary,
              and informed
            </li>
            <li>
              You are not relying on any representation or promise by ATM.com
              regarding electronic communications other than what is expressly
              stated in this Section 9
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.2 Hardware and Software Requirements
          </h3>
          <p>
            In order to use the Services and to access and retain our Electronic
            Communications, you need to have, and you represent and warrant to
            us that you do have, and you consent and agree that at all times you
            will have, all of the following: (1) a valid email address; (2) a
            computer, laptop, tablet, smartphone or other device that is
            Internet-enabled and is connected to the internet: (3) a web browser
            that includes 128-bit encryption, such as a Current Version of
            Chrome, Microsoft Edge, Firefox or Safari, with cookies enabled; (4)
            a Current Version of a program that accurately reads and displays
            PDF files, such as Adobe Acrobat Reader; (5) a computer or device
            and an operating system capable of supporting all of the above; and
            (6) data storage to electronically save Communications or an
            installed printer to print them.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.3 Use of a Virtual Private Network (VPN) or Proxy Server
          </h3>
          <p>
            A virtual private network (VPN) is a service that can encrypt and
            route certain network traffic from your device in a way that may
            hide your IP address from the websites and services you visit,
            including the ability to determine your physical location. A proxy
            server acts as a gateway between you and the internet – it’s an
            intermediary that separates users from the websites and apps they
            visit primarily for the purpose of data security, network
            performance, and privacy. The use of the ATM app is strictly limited
            to users in the Service Area. Using a VPN or proxy server to
            interact with the ATM App may result in the App blocking your use
            and access to some or all Services.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.4. Withdrawal of Consent
          </h3>
          <p>
            You may withdraw your consent to receive Electronic Communications
            by contacting us. Such withdrawal will be effective only after we
            have had a reasonable time period to process such withdrawal and it
            will not apply to Electronic Communications provided by us to you
            before the withdrawal of your consent becomes effective. If you
            withdraw your consent, then, except as may be expressly provided
            otherwise in this Agreement, (a) we will terminate your access to,
            and your ability to use, the Service, and (b) we will close any
            account that you may have with us and will return to you any balance
            that you may have therein. Withdrawal of consent does not, however,
            restrict the sending of communications required by any court or
            legal jurisdiction or governmental regulatory agency.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.5 Changes
          </h3>
          <p>
            We reserve the right, in our sole discretion, (a) to communicate
            with you in paper form and/or (b) to discontinue to provide you with
            Electronic Communications, and/or (c) to terminate our Agreement and
            conditions with respect to Electronic Signatures and Electronic
            Communications. Subject to any applicable law, we may notify you of
            any such discontinuance, termination by means of Electronic
            Communications, by updating this Agreement on the Site or the
            Services, or by delivering notice by email or text message, or by
            any other means of Electronic Communications that we may choose in
            our discretion.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            9.6 Contacting Us Regarding Electronic Signatures and Electronic
            Communications
          </h3>
          <p>
            Please feel free to request information, to request a paper copy of
            any Electronic Communication that we provided to you, or ask
            questions regarding our policy on Electronic Signatures and
            Electronic Communications by contacting us.
          </p>
          <h2
            id="messaging"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            10. MESSAGING
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.1 Overview
          </h3>
          <p>
            As part of the Services, ATM.com may communicate with you through
            various messaging channels, including email, SMS/text messages, push
            notifications, and in-app notices. This Section 10 describes the
            types of messages you may receive, how you can opt out of certain
            categories of messages, and your rights and responsibilities in
            connection with ATM.com's messaging practices. ATM.com's messaging
            practices are governed by the Telephone Consumer Protection Act, 47
            U.S.C. § 227 et seq. ("TCPA"), the CAN-SPAM Act, and other
            applicable federal and state laws.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.2 Categories of Messages
          </h3>
          <p>
            ATM.com sends two distinct categories of messages, each governed by
            different consent and opt-out rules:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Transactional and Account Messages. These are messages that
              ATM.com is required or permitted to send in connection with your
              Account and your use of the Services, regardless of your marketing
              preferences. They include, but are not limited to: account
              registration and verification messages; transaction confirmations
              and receipts; ATM Instant Cash advance confirmations and repayment
              notices; error resolution communications under Section 6; security
              alerts and suspicious activity notifications; notices of material
              changes to these Terms or our Privacy Policy; legal notices
              including arbitration-related communications; and any other
              Communication required by applicable law or necessary to
              administer your Account. You may not opt out of Transactional and
              Account Messages while your Account remains active, as these
              communications are a necessary part of the Services. Opting out of
              Transactional and Account Messages will be treated as a withdrawal
              of consent to electronic communications under Section 9.4 and will
              result in termination of your Account.
            </li>
            <li>
              Marketing and Promotional Messages. These are messages that
              ATM.com sends to inform you about new features, offers, earning
              opportunities, and other promotional content that ATM.com believes
              may be of interest to you. Marketing and Promotional Messages are
              sent only with your prior express written consent, as required by
              applicable law. Your consent to receive Marketing and Promotional
              Messages is separate from, and independent of, your consent to
              receive Transactional and Account Messages. You do not need to
              consent to Marketing and Promotional Messages in order to register
              for or use the Services. If you do consent, you may withdraw that
              consent at any time as described in Section 10.4 below, without
              affecting your ability to use the Services or your receipt of
              Transactional and Account Messages.
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.3 SMS and Text Message Program; Required Disclosures
          </h3>
          <p>
            By providing your mobile phone number and consenting to receive SMS
            messages from ATM.com, you agree to receive text messages at the
            mobile number you provide. The following disclosures apply to
            ATM.com's SMS messaging program:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Message Frequency. For Transactional and Account Messages, message
              frequency varies based on your Account activity and the nature of
              the transaction or event triggering the message. ATM.com will not
              send Marketing and Promotional Messages at a frequency that a
              reasonable person would consider excessive or harassing.
            </li>
            <li>
              Message and Data Rates. Standard message and data rates from your
              wireless carrier may apply to all SMS messages, whether
              Transactional or Marketing. ATM.com is not responsible for any
              charges imposed by your wireless carrier in connection with SMS
              messages. If you have questions about your text or data plan,
              contact your wireless carrier directly.
            </li>
            <li>
              Supported Carriers. ATM.com's SMS messaging service is available
              through most major U.S. wireless carriers. Carriers are not liable
              for delayed or undelivered messages. Message delivery is subject
              to effective transmission by your carrier and ATM.com cannot
              guarantee delivery of every SMS message.
            </li>
            <li>
              Compatible Devices. SMS messaging requires a mobile device capable
              of receiving standard SMS text messages.
            </li>
            <li>
              SMS Consent is Number-Specific. Your consent to receive SMS
              messages applies to the specific mobile phone number you provide
              to ATM.com at the time of consent. ATM.com will not send SMS
              messages to a number for which it does not have valid consent.
            </li>
            <li>
              Consent is Not Transferable. Your consent to receive SMS messages
              from ATM.com is personal to you and applies only to your ATM.com
              Account. It may not be transferred or assigned to any other person
              or account.
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.4 How to Opt Out of Marketing and Promotional Messages
          </h3>
          <p>
            You may opt out of Marketing and Promotional Messages at any time
            through any of the following methods, each of which is equally
            valid:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Reply STOP. To opt out of SMS Marketing and Promotional Messages,
              reply STOP to any marketing SMS message you receive from ATM.com.
              Upon receipt of a STOP reply, ATM.com will send you a single
              confirmation message acknowledging your opt-out, after which no
              further Marketing and Promotional SMS messages will be sent to
              that number. This is the fastest opt-out method for SMS messages.
            </li>
            <li>
              Reply HELP. To receive information about ATM.com's SMS messaging
              program, including opt-out instructions, reply HELP to any SMS
              message from ATM.com. ATM.com will respond with program
              information and a reminder of how to opt out.
            </li>
            <li>
              Email. To opt out of email Marketing and Promotional Messages,
              click the "unsubscribe" link included in any marketing email from
              ATM.com, or send an opt-out request to{' '}
              <a href="mailto:opt-out@atm.com" className="underline">
                opt-out@atm.com
              </a>{' '}
              from the email address associated with your Account.
            </li>
            <li>
              In-App Settings. You may manage your messaging preferences,
              including opting out of Marketing and Promotional Messages,
              through the notification settings in the App.
            </li>
            <li>
              Written Request. You may submit a written opt-out request by mail
              to ATM.com, Inc., P.O. Box 7475, Newport Beach, CA 92658-7475,
              Attn: Messaging Opt-Out. Written opt-out requests will be
              processed within ten (10) business days of receipt.
            </li>
          </ul>
          <p>
            Opting out of Marketing and Promotional Messages through any of the
            above methods will not affect your receipt of Transactional and
            Account Messages, which will continue as long as your Account
            remains active. ATM.com will honor opt-out requests within the
            timeframe required by applicable law and in no event later than ten
            (10) business days after the opt-out is received, except that STOP
            replies to SMS messages will be processed immediately.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.5 Separate Consent for Each Purpose
          </h3>
          <p>
            Your consent to receive SMS messages from ATM.com is
            purpose-specific. Specifically:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Your consent to receive Transactional and Account Messages does
              not constitute consent to receive Marketing and Promotional
              Messages, and vice versa.
            </li>
            <li>
              Your consent to receive SMS messages does not constitute consent
              to receive messages through other channels (such as email or push
              notifications), and vice versa.
            </li>
            <li>
              If ATM.com wishes to use your mobile number for a materially
              different purpose than the purpose for which you originally
              provided consent, ATM.com will obtain a new, separate consent from
              you before sending messages for that new purpose.
            </li>
          </ul>
          <p>
            This Section 10.5 is intended to ensure compliance with the TCPA's
            requirement of separate, purpose-specific prior express written
            consent for autodialed or prerecorded marketing calls and texts.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.6 Your Representations Regarding Your Mobile Number
          </h3>
          <p>
            By providing a mobile phone number to ATM.com, you certify,
            represent, and warrant that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The mobile phone number you have provided is your own personal
              number and not a number belonging to any other person
            </li>
            <li>
              You are the account holder or are authorized by the account holder
              to provide consent for that number
            </li>
            <li>
              You are permitted to receive text messages at the number you have
              provided
            </li>
            <li>
              The number you have provided is not a number that has been
              reassigned from a prior account holder who may have had a
              different relationship with ATM.com
            </li>
            <li>
              You will promptly notify ATM.com at{' '}
              <a href="mailto:support@atm.com" className="underline">
                support@atm.com
              </a>{' '}
              if you change your mobile phone number, port your number to a
              different carrier, or otherwise stop using the number you have
              provided, so that ATM.com can update its records and cease sending
              messages to that number.
            </li>
          </ul>
          <p>
            You acknowledge that ATM.com's TCPA liability exposure is directly
            affected by the accuracy of the representations you make in this
            Section 10.6, and that providing inaccurate information about your
            mobile number may result in harm to third parties who receive
            messages intended for you. You agree to indemnify and hold ATM.com
            harmless from and against any and all claims, liabilities, damages,
            losses, and expenses, including reasonable attorneys' fees, arising
            from or related to your breach of any representation in this Section
            10.6.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.7 Do-Not-Call Registry
          </h3>
          <p>
            ATM.com respects the National Do-Not-Call Registry maintained by the
            Federal Trade Commission. If your mobile number is registered on the
            National Do-Not-Call Registry, ATM.com will not send you unsolicited
            Marketing and Promotional SMS messages unless you have provided
            ATM.com with prior express written consent to do so. Registration on
            the Do-Not-Call Registry does not affect your receipt of
            Transactional and Account Messages.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.8 Autodialer Disclosure
          </h3>
          <p>
            When signing up for the Services and providing your mobile phone
            number, you acknowledge and agree that ATM.com may use an automatic
            telephone dialing system ("autodialer") or artificial or prerecorded
            voice technology to send you SMS messages, to the extent permitted
            by applicable law and consistent with the consent you have provided
            under this Section 10. This disclosure is provided in compliance
            with the TCPA. You have the right to revoke your consent to receive
            autodialed or prerecorded messages at any time by following the
            opt-out procedures described in Section 10.4.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.9 Modification or Termination of Messaging Services
          </h3>
          <p>
            ATM.com reserves the right to modify, suspend, or terminate its SMS
            messaging program or any other messaging service at any time, for
            any reason, with or without notice, without liability to you,
            provided that ATM.com will make reasonable efforts to notify you of
            any material change to its messaging program through an alternative
            communication channel where practicable. Modification or termination
            of the messaging program does not affect any other aspect of the
            Services or these Terms.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            10.10 Third-Party Messaging Providers
          </h3>
          <p>
            ATM.com may use third-party service providers to deliver SMS and
            other messages on its behalf. These providers act as ATM.com's
            agents and are subject to confidentiality and data protection
            obligations. ATM.com remains responsible for ensuring that messages
            sent through third-party providers comply with applicable law and
            these Terms. Your mobile number and messaging preferences will be
            shared with these providers solely for the purpose of delivering
            messages on ATM.com's behalf and will not be used by them for their
            own independent marketing purposes.
          </p>
          <h2
            id="security-incident-response"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            11. SECURITY AND INCIDENT RESPONSE
          </h2>
          <p>
            ATM.com maintains administrative, technical, and physical safeguards
            designed to protect user information.
          </p>
          <p>
            In the event of a security incident involving personal information,
            ATM.com will respond in accordance with applicable data protection
            laws, including notification requirements where required.
          </p>
          <p>
            No system can guarantee absolute security. Users share
            responsibility for protecting credentials and devices.
          </p>
          <h2
            id="chatbots-ai"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            12. USE OF CHATBOTS AND ARTIFICIAL INTELLIGENCE
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.1 Overview
          </h3>
          <p>
            To provide efficient customer service and improve user experience,
            ATM.com may use automated systems, including chatbots and artificial
            intelligence ("AI") tools, to communicate with you and assist with
            support inquiries. This Section 12 describes how we use these
            technologies, what limitations apply to AI-generated communications,
            and what rights you have in connection with our use of AI.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.2 Limitations of AI-Generated Communications
          </h3>
          <p>
            AI- and chatbot-generated communications are provided for general
            informational and customer service purposes only. You acknowledge
            and agree that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              AI communications do not constitute professional advice of any
              kind. Nothing communicated to you by an AI system or chatbot
              through the Services shall be construed as legal advice, financial
              advice, investment advice, tax advice, accounting advice, medical
              advice, or any other form of professional advice. This limitation
              applies regardless of the subject matter of the AI-generated
              communication and regardless of how the communication is phrased.
            </li>
            <li>
              AI systems may make errors. AI- and chatbot-generated responses
              are generated automatically based on patterns in data and
              pre-programmed parameters. They may be inaccurate, incomplete,
              outdated, or inapplicable to your specific situation. ATM.com does
              not warrant the accuracy, completeness, or reliability of any
              AI-generated communication.
            </li>
            <li>
              You should verify critical information. Before relying on any
              AI-generated communication for any purpose — including but not
              limited to making financial decisions, taking action on your
              account, or evaluating your legal rights — you should verify the
              information with a human ATM.com representative or, where
              appropriate, with an independent qualified professional.
            </li>
            <li>
              AI systems are not authorized to modify your account terms. No AI-
              or chatbot-generated communication shall be construed as an
              amendment to, or waiver of, any provision of these Terms, our
              Privacy Policy, or any other agreement between you and ATM.com.
              Only written modifications made in accordance with Section 13.2 of
              these Terms are binding on ATM.com.
            </li>
            <li>
              AI systems are not authorized to make binding commitments. No
              statement, representation, or promise made by an AI system or
              chatbot shall be binding on ATM.com unless confirmed in writing by
              an authorized human representative of ATM.com.
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.3 No Attorney-Client or Other Privileged Relationship
          </h3>
          <p>
            Communications between you and ATM.com's AI systems or chatbots do
            not establish, and shall not be construed as establishing, any
            attorney-client relationship, financial advisory relationship, or
            any other professional relationship between you and ATM.com or any
            of its employees, agents, or representatives. Nothing communicated
            to an AI system or chatbot is protected by attorney-client
            privilege, work product protection, or any other evidentiary
            privilege. You should not share information with ATM.com's AI
            systems that you wish to keep legally privileged or that you would
            only share with a licensed professional under a duty of
            confidentiality.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.4 Session Recording, Data Use, and Privacy
          </h3>
          <p>
            AI and chatbot interactions may be recorded, logged, and stored by
            ATM.com and its service providers. You consent to such recording and
            storage by using the Services. Specifically:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Interaction data may include the full content of your
              communications with AI systems, timestamps, device identifiers,
              and related metadata.
            </li>
            <li>
              Recorded interactions may be used by ATM.com to improve the
              quality and accuracy of AI systems, ensure compliance with these
              Terms, investigate disputes, maintain security, and fulfill legal
              obligations.
            </li>
            <li>
              ATM.com will not sell, rent, or otherwise share chatbot or AI
              session data with unaffiliated third parties for their own
              marketing purposes, except as required by applicable law or as
              permitted under our Privacy Policy.
            </li>
            <li>
              AI interaction data shared with third-party AI service providers
              is shared solely for the purpose of operating and improving the
              Services and is subject to confidentiality and data protection
              obligations. A description of the categories of third-party AI
              service providers that may process your interaction data is
              available in our Privacy Policy.
            </li>
            <li>
              If you are a California resident, your rights under the CCPA with
              respect to AI interaction data are the same as your rights with
              respect to other personal information collected by ATM.com, as
              described in Section 27 of these Terms and in our Privacy Policy.
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.5 Third-Party AI Platforms
          </h3>
          <p>
            ATM.com may use third-party AI platforms and large language model
            providers to power some or all of its AI and chatbot features. The
            specific third-party AI platforms we use may change from time to
            time. By using the Services, you acknowledge that your interactions
            with AI-powered features may be processed by third-party AI
            providers subject to those providers' own terms of service and
            privacy policies. ATM.com will maintain a current list of material
            third-party AI service providers in our Privacy Policy.
          </p>
          <p>
            ATM.com is not responsible for the independent acts or omissions of
            third-party AI platform providers, provided that ATM.com has
            exercised reasonable care in selecting and monitoring such
            providers.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.6 Regulatory Compliance; AI Disclosure Laws
          </h3>
          <p>
            ATM.com is committed to complying with all applicable federal and
            state laws governing the use of AI and automated decision-making
            systems, including but not limited to applicable provisions of
            California law governing automated communications and bot
            disclosures. To the extent any applicable law requires disclosures
            or provides rights in connection with ATM.com's use of AI systems
            that are not addressed in this Section 12, ATM.com will provide such
            disclosures and honor such rights as required.
          </p>
          <p>
            If you believe ATM.com has failed to identify an automated
            interaction as required by applicable law, please contact us at{' '}
            <a className="underline" href="mailto:support@atm.com">
              support@atm.com
            </a>
            .
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.7 Limitation of Liability for AI Communications
          </h3>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, ATM.COM SHALL NOT
            BE LIABLE FOR ANY LOSS, DAMAGE, OR HARM ARISING FROM YOUR RELIANCE
            ON ANY AI- OR CHATBOT-GENERATED COMMUNICATION, INCLUDING BUT NOT
            LIMITED TO ANY INACCURACY, ERROR, OMISSION, OR MISUNDERSTANDING IN
            AN AI-GENERATED RESPONSE. YOUR USE OF AI-FACILITATED CUSTOMER
            SERVICE FEATURES IS AT YOUR OWN RISK. THIS LIMITATION OF LIABILITY
            IS IN ADDITION TO, AND DOES NOT LIMIT, THE GENERAL LIMITATION OF
            LIABILITY SET FORTH IN SECTION 17 OF THESE TERMS.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            12.8 Changes to AI Features
          </h3>
          <p>
            ATM.com reserves the right to modify, expand, reduce, or discontinue
            its use of AI and chatbot systems at any time without notice.
            Changes to how AI is used in the Services are subject to the general
            modification provisions of Section 13.2 of these Terms, except that
            such changes do not constitute a Material Change to Section22 and do
            not require affirmative acceptance unless they independently affect
            your arbitration rights.
          </p>
          <h2
            id="service-availability"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            13. SERVICE AVAILABILITY
          </h2>
          <p>
            Although it is the intention of ATM.com for the Services to be
            available as much as possible, there will be occasions when the
            Services may be interrupted, including, without limitation, for
            scheduled maintenance or upgrades, for emergency repairs, or due to
            failure of telecommunications links and/or equipment.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            13.1 Changes to the Services and Terms; Corrections
          </h3>
          <p>
            ATM.com is constantly evolving to provide the best possible
            experience for all of our customers, and we reserve the right to
            change and improve the features and functionality of the Services at
            any time and at our sole discretion. This includes adding, modifying
            or removing specific features and functionality of the Services.
            Additionally, we reserve the right to suspend or stop the Services
            altogether.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            13.2 Modifications to Terms of Service
          </h3>
          <p>
            We may, from time to time, modify these Terms. Please check this
            page periodically for modifications. In addition, we may notify you
            by email or by in-app messaging to your ATM.com Account of any
            material modification to these Terms. If you do not agree to, or
            cannot comply with, the modified Terms, you must stop using the
            Services. Subject to the exception set forth below regarding
            modifications to Section 22, the modified Terms will take effect
            after their posting on their effective date, and will apply on a
            going-forward basis, unless otherwise provided in a notice to you.
            Your continued use of the Services after any such modification
            constitutes your binding acceptance of such changes.
          </p>
          <p>
            <span className="font-bold">
              Modifications to Section 22 (Mandatory Arbitration and Class
              Action Waiver) are governed exclusively by Section 22.11 and not
              by this Section 13.2.
            </span>{' '}
            Notwithstanding anything to the contrary in this Section 13.2, your
            continued use of the Services alone shall never constitute
            acceptance of any Material Change (as defined in Section 22.11(B))
            to Section 22. Any Material Change to Section 22 requires your
            affirmative acceptance as described in Section 22.11(C) before it
            becomes binding on you. In the event of any conflict between this
            Section 13.2 and Section 22.11 with respect to modifications to the
            arbitration and class action waiver provisions, Section 22.11 shall
            control in all respects.
          </p>
          <p>
            For the avoidance of doubt, this Section 13.2 continues to govern
            all modifications to these Terms other than modifications to Section
            22, and the affirmative acceptance requirement described in Section
            22.11(C) applies only to Material Changes to Section 22 and to no
            other provisions of these Terms.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            13.3 Right to Update
          </h3>
          <p>
            There may be information regarding the Services and these Terms that
            contain typographical errors, inaccuracies, or omissions, including
            descriptions, pricing, availability, and various other information.
            We reserve the right to correct any errors, inaccuracies or
            omissions and to change or update the information at any time,
            without prior notice.
          </p>
          <h2
            id="legal-compliance"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            14. LEGAL COMPLIANCE
          </h2>
          <p>
            You acknowledge, consent, and agree that ATM.com may access,
            preserve, and disclose content you provide us if required to do so
            by law or in a good faith belief that such access, preservation, or
            disclosure is permitted by our Privacy Policy or reasonably
            necessary or appropriate for any of the following reasons: (1) to
            comply with legal process (e.g., a subpoena, court order, or
            warrant); (2) to enforce these Terms; (3) to respond to claims that
            any content violates the rights of third parties; (4) to protect the
            rights, property, or personal safety of ATM.com, its agents and
            affiliates, its users, and the public; or (5) to address your
            requests.
          </p>
          <h2
            id="indemnification"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            15. INDEMNIFICATION
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            15.1 Your Indemnification
          </h3>
          <p>
            TO THE FULLEST EXTENT PERMITTED BY LAW, YOU AGREE TO INDEMNIFY,
            DEFEND, AND HOLD ATM.COM AND ITS RESPECTIVE OFFICERS, DIRECTORS,
            EMPLOYEES, MEMBERS, SHAREHOLDERS, OR REPRESENTATIVES (AND ALL
            SUCCESSORS AND ASSIGNS OF ANY OF THE FOREGOING), HARMLESS FROM AND
            AGAINST ANY CLAIM, LOSS, OR DEMAND, INCLUDING WITHOUT LIMITATION,
            REASONABLE ATTORNEYS' FEES AND DISBURSEMENTS, MADE IN CONNECTION
            WITH OR ARISING OUT OF YOUR VIOLATION OF THESE TERMS OR OUR PRIVACY
            POLICY, AND/OR YOUR SUBMISSION, POSTING, OR TRANSMISSION OF CONTENT
            TO THE SERVICES. WE RESERVE THE RIGHT, AT OUR OWN EXPENSE, TO ASSUME
            THE EXCLUSIVE DEFENSE AND CONTROL OF SUCH DISPUTES, AND IN ANY
            EVENT, YOU WILL COOPERATE WITH US IN ASSERTING ANY AVAILABLE
            DEFENSES.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            15.2 ATM.com’s Limited Reciprocal Indemnification
          </h3>
          <p>
            To the fullest extent permitted by applicable law, ATM.com agrees to
            indemnify, defend, and hold you harmless from and against any
            third-party claim, loss, or demand, including reasonable attorneys'
            fees, arising directly and proximately from ATM.com's own gross
            negligence or willful misconduct in connection with the Services.
            This Section 15.2 does not apply to claims arising from your own
            acts or omissions, from the acts or omissions of third parties over
            whom ATM.com has no control, from force majeure events as described
            in Section 26, or from any matter for which your indemnification
            obligation under Section 15.1 applies. ATM.com's obligation under
            this Section 15.2 is subject to the limitation of liability set
            forth in Section 17 of these Terms.
          </p>
          <h2
            id="disclaimer"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            16. DISCLAIMER OF REPRESENTATIONS AND WARRANTIES
          </h2>
          <p>
            YOU AGREE THAT YOUR USE OF THE ATM.COM SITE AND SERVICES IS AT YOUR
            OWN RISK. EFFORTS BY ATM.COM TO MODIFY THE SITE SHALL NOT BE DEEMED
            A WAIVER OF THESE LIMITATIONS. THE SITE, INCLUDING ALL CONTENT,
            INFORMATION OR SERVICES PROVIDED THROUGH, OR IN CONJUNCTION WITH,
            THE SITE, ARE PROVIDED "AS IS."
          </p>
          <p>
            WE DISCLAIM ALL WARRANTIES AND REPRESENTATIONS, EITHER EXPRESS OR
            IMPLIED, WITH RESPECT TO ATM.COM AND THE SERVICES, INCLUDING,
            WITHOUT LIMITATION, (1) ANY WARRANTIES OF MERCHANTABILITY OR FITNESS
            FOR A PARTICULAR PURPOSE, (2) OF INFORMATIONAL CONTENT OR ACCURACY,
            (3) OF NON-INFRINGEMENT, (4) OF PERFORMANCE, (5) OF TITLE, (6) THAT
            THE SITE WILL OPERATE IN AN ERROR FREE, TIMELY, SECURE, OR
            UNINTERRUPTED MANNER, IS CURRENT AND UP TO DATE AND ACCURATELY
            DESCRIBES ATM.COM’S PRODUCTS AND SERVICES, OR IS FREE OF VIRUSES OR
            OTHER HARMFUL COMPONENTS, (7) THAT ANY DEFECTS OR ERRORS IN THE SITE
            WILL BE CORRECTED, OR (8) THAT THE SITE IS COMPATIBLE WITH ANY
            PARTICULAR HARDWARE OR SOFTWARE PLATFORM.
          </p>
          <h2
            id="liability"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            17. LIMITATION OF LIABILITY
          </h2>
          <p>
            IN NO EVENT SHALL ATM.COM OR ITS OFFICERS, DIRECTORS, AGENTS,
            EMPLOYEES, REPRESENTATIVES, INTERNAL OPERATING UNITS, AFFILIATES,
            SUBSIDIARIES, SUBLICENSEES, SUCCESSORS AND ASSIGNS, INDEPENDENT
            CONTRACTORS, AND RELATED PARTIES (COLLECTIVELY, WITH ATM.COM, INC.,
            THE “ATM ENTITIES”) BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY LOSS
            OF PROFITS, LOSS OF USE, LOSS OF DATA, INTERRUPTION OF BUSINESS, OR
            ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL OR CONSEQUENTIAL DAMAGES
            ARISING OUT OF OR IN ANY WAY CONNECTED WITH THE USE OF THE SITE OR
            WITH THE DELAY OR INABILITY TO USE SAME, OR FOR ANY BREACH OF
            SECURITY ASSOCIATED WITH THE TRANSMISSION OF SENSITIVE INFORMATION
            THROUGH THE SITE, OR FOR ANY INFORMATION, PRODUCTS, AND SERVICES
            OBTAINED THROUGH OR VIEWED ON THE SITE, OR OTHERWISE ARISING OUT OF
            THE USE OF SAME, WHETHER BASED ON CONTRACT, TORT, STRICT LIABILITY,
            REGULATION, COMMON LAW PRECEDENT OR OTHERWISE, EVEN IF ATM.COM HAS
            BEEN ADVISED OF THE POSSIBILITY OF DAMAGES AND EVEN IF SUCH DAMAGES
            RESULT FROM ATM.COM’S NEGLIGENCE OR GROSS NEGLIGENCE. IN NO EVENT
            WILL ATM.COM TOTAL LIABILITY TO YOU FOR ALL DAMAGES, LOSSES OR
            CAUSES OF ACTION TO THE GREATER OF $500 OR THE FEES PAID BY THE USER
            IN THE PRECEDING 12 MONTHS.
          </p>
          <p>
            BECAUSE SOME STATES/JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR
            LIMITATION OF LIABILITY FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES, THE
            ABOVE LIMITATION MAY NOT APPLY TO YOU. IN SUCH STATES, LIABILITY OF
            ATM ENTITIES SHALL BE LIMITED TO THE FULLEST EXTENT PERMITTED BY
            LAW. ADDITIONAL DISCLAIMERS APPEAR WITHIN THE BODY OF THE SITE AND
            ARE INCORPORATED HEREIN BY REFERENCE. TO THE EXTENT ANY SUCH
            DISCLAIMERS PLACE GREATER RESTRICTIONS ON YOUR USE OF THE SITE OR
            THE MATERIAL CONTAINED THEREIN, SUCH GREATER RESTRICTIONS SHALL
            APPLY. THIS LIMITATION OF LIABILITY SHALL APPLY TO THIRD PARTY
            CLAIMS AS WELL AS CLAIMS BETWEEN THE PARTIES.
          </p>
          <p>
            IF APPLICABLE LAW DOES NOT ALLOW ALL OR ANY PART OF THE ABOVE
            LIMITATION OF LIABILITY TO APPLY TO YOU, THE LIMITATIONS WILL APPLY
            TO YOU ONLY TO THE EXTENT PERMITTED BY APPLICABLE LAW. IF YOU ARE A
            NEW JERSEY RESIDENT, OR A RESIDENT OF ANOTHER STATE THAT PERMITS THE
            EXCLUSION OF THESE WARRANTIES AND LIABILITIES, THEN THE ABOVE
            LIMITATIONS SPECIFICALLY DO APPLY TO YOU.
          </p>
          <p>
            NOTWITHSTANDING THE FOREGOING, THE LIMITATIONS IN THIS SECTION 17 DO
            NOT APPLY TO ATM.COM’S LIABILITY UNDER THE ELECTRONIC FUND TRANSFER
            ACT (15 U.S.C. § 1693 ET SEQ.) AND REGULATION E (12 C.F.R. PART
            1005), OR TO ANY OTHER LIABILITY THAT CANNOT BE LIMITED OR EXCLUDED
            UNDER APPLICABLE FEDERAL OR STATE LAW.
          </p>
          <h2
            id="legal-notices"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            18. LEGAL NOTICES AND SPECIAL DISCLAIMERS
          </h2>
          <p>
            ATM.COM® is a registered trademark and ANT MONEY™ is an
            unregistered, common-law trademark of ATM.com, Inc. All other
            trademarks are the property of their respective owners. GOOGLE
            PLAY® and the Google Play logo are registered trademarks of Google,
            Inc. APPLE®, the APPLE logo, and IPHONE® are registered trademarks
            of Apple, Inc.
          </p>
          <p>
            ATM.COM IS NOT A BROKER DEALER, WE ARE NOT AN INVESTMENT ADVISER,
            AND WE DO NOT PURPORT TO AND DO NOT, IN ANY FASHION, PROVIDE
            INVESTMENT ADVICE, TAX, ACCOUNTING, ACTUARIAL, RECORDKEEPING, LEGAL,
            OR ANY RELATED SERVICES OR ADVICE. NO CONTENT, MATERIALS, OR
            INSTRUCTIONS SHOULD BE UNDERSTOOD AS CONSTITUTING A RECOMMENDATION
            TO ENTER IN ANY SECURITIES TRANSACTION. WE HAVE NO ACCESS TO
            NON-PUBLIC INFORMATION ABOUT PUBLICLY TRADED COMPANIES, AND WE ARE
            NOT REGULATED BY THE U.S. SECURITIES AND EXCHANGE COMMISSION.
          </p>
          <p>
            INFORMATION PROVIDED BY ATM.COM CUSTOMER SUPPORT IS FOR
            INFORMATIONAL AND GENERAL EDUCATIONAL PURPOSES ONLY AND IS NOT
            INVESTMENT OR FINANCIAL ADVICE, OR ANY OTHER TYPE OF PROFESSIONAL
            ADVICE. ATM.com does not make any guarantees or representations that
            your portion of the ATM.com Account will grow to a specific amount
            or that the Services are appropriate for you and your financial
            situation.
          </p>
          <h2
            id="proprietary-content"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            19. ATM.COM PROPRIETARY CONTENT
          </h2>
          <p>
            We offer the Services and the features, information, materials, and
            content provided and depicted through the Services (collectively,
            "ATM.com Content") solely for your personal use for the purposes
            described therein and in these Terms. Any and all other uses are
            prohibited. You may not restrict or inhibit any other person from
            using or enjoying the Services or ATM.com Content.
          </p>
          <p>
            ATM.com Content is protected by copyright, trademark, patent, trade
            secret and other laws, and, as between you and ATM.com, ATM.com owns
            and retains all rights in the ATM.com Content and the Service, with
            the exception of content that ATM.com has licensed from third
            parties, which is owned by those third parties. You will not remove,
            alter or conceal any copyright, trademark, service mark or other
            proprietary rights notices incorporated in or accompanying the
            ATM.com Content. You may not adapt or use otherwise any name, mark
            or logo that is identical, or confusingly similar to any of our
            trademarks, service marks and logos. You must avoid any action or
            omission which may dilute or tarnish our goodwill.
          </p>
          <p>
            Unless otherwise expressly permitted in the Terms, you may not copy,
            distribute, display or perform publicly, sublicense, decompile,
            disassemble, reduce to human readable form, execute publicly, make
            available to the public, adapt, make commercial use, process,
            compile, translate, sell, lend, rent, reverse engineer, combine with
            other software, modify or create derivative works of any of the
            ATM.com Content, either by yourself or by a third party on your
            behalf, in any way or by any means, including, but not limited to
            electronic, mechanical or optical means, without prior written
            authorization from the Company.
          </p>
          <p>
            The ATM.com name and logo are registered trademarks of ATM.com, and
            may not be copied, imitated or used, in whole or in part, without
            the prior written permission of ATM.com. In addition, all page
            headers, custom graphics, button icons and scripts are service
            marks, trademarks and/or trade dress of ATM.com, and may not be
            copied, imitated or used, in whole or in part, without prior written
            permission from ATM.com.
          </p>
          <h2
            id="copyright"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            20. COPYRIGHT INFRINGEMENT CLAIMS
          </h2>
          <p>
            ATM.com respects the rights of all copyright holders and in this
            regard, ATM.com has adopted and implemented a policy that provides
            for the termination in appropriate circumstances of users and
            account holders who infringe the rights of copyright holders. If you
            believe that your work has been copied in a way that constitutes
            copyright infringement, please provide ATM.com 's Copyright Agent
            the following information required by the Online Copyright
            Infringement Liability Limitation Act of the Digital Millennium
            Copyright Act, 17 U.S.C. 512:
          </p>
          <ul className="list-disc pl-6">
            <li>
              A physical or electronic signature of a person authorized to act
              on behalf of the owner of an exclusive right that is allegedly
              infringed
            </li>
            <li>
              Identification of the copyright work claimed to have been
              infringed, or, if multiple copyrighted works at a single online
              site are covered by a single notification, a representative list
              of such works at that site
            </li>
            <li>
              Identification of the material that is claimed to be infringing or
              to be the subject of infringing activity and that is to be removed
              or access to which is to be disabled, and information reasonably
              sufficient to permit us to locate the material
            </li>
            <li>
              Information reasonably sufficient to permit us to contact the
              complaining party
            </li>
            <li>
              A statement that the complaining party has a good-faith belief
              that use of the material in the manner complained of is not
              authorized by the copyright owner, its agent, or the law
            </li>
            <li>
              A statement that the information in the notification is accurate,
              and under penalty of perjury, that the complaining party is
              authorized to act on behalf of the owner of an exclusive right
              that is allegedly infringed
            </li>
            <li>
              For copyright inquiries under the Digital Millennium Copyright Act
              please contact:
            </li>
          </ul>
          <p className="px-16">
            <span className="block">DMCA Copyright Agent</span>
            <span className="block">ATM.com, Inc.</span>
            <span className="block">P.O. Box 7475</span>
            <span className="block">Newport Beach, CA 92658-7475</span>
            <span className="block">
              Email:{' '}
              <a className="underline" href="mailto:dmca@ATM.com">
                dmca@ATM.com
              </a>
            </span>
          </p>
          <h2
            id="application-license"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            21. APPLICATION LICENSE AND DEVICE MANUFACTURER TERMS
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            21.1 License Grant for Application
          </h3>
          <p>
            Subject to your compliance with these Terms, we grant you a limited
            non-exclusive, non-transferable license (i) to download and install
            a copy of our mobile App on a device that you exclusively control,
            and (ii) to run such copy of the App solely for your own personal,
            non-commercial use. We reserve all rights in and to the App not
            expressly granted to you under these Terms. You may not separate
            component parts of the App. You may not distribute the App or
            transfer the App to another person. Running the App on a rooted
            (Android) or jailbroken (iOS) device, or any device that has been
            modified to bypass the manufacturer's or operating system provider's
            standard security controls or software installation restrictions, is
            not allowed and may result in your account being suspended or
            terminated. ATM.com does not support, test, or warrant the
            performance or security of the App on any such modified device.
            ATM.com expressly disclaims any liability for data loss, security
            breaches, unauthorized account access, or any other harm arising
            from your use of the App on a rooted or jailbroken device. If
            ATM.com detects or reasonably suspects that the App is being
            operated on a rooted or jailbroken device, ATM.com reserves the
            right to restrict or suspend your access to some or all of the
            Services without notice.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            21.2 iOS and Google Terms
          </h3>
          <p>
            If you have downloaded our App, you agree to promptly download and
            install any new version that we make available through the iTunes
            App Store or Google Play store, as applicable. Some new versions may
            contain updated Terms. Some new versions may contain security fixes
            and service improvements, whether or not we disclose that they do;
            accordingly, failure to promptly update your version of the App may
            in some cases expose you to increased security risks or Website
            malfunctions.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            21.3 Additional iOS Terms
          </h3>
          <p>
            You acknowledge and agree that (i) these Terms are binding between
            you and ATM.com only, and Apple is not a party hereto, and (ii) as
            between ATM.com and Apple, it is ATM.com that is responsible for the
            App and the content thereof. You must use the iOS app only on an
            Apple-branded product that runs iOS. Your use of the App must comply
            with the terms of use applicable to the Apple source from which you
            obtain it (including the Usage Rules set forth in the Apple App
            Store Terms of Service). You acknowledge that Apple has no
            obligation to furnish you with any maintenance and support platform
            with respect to the App.
          </p>
          <p>
            You acknowledge that Apple is not responsible for addressing any
            claims you have or any claims of any third party relating to the App
            or your possession and use of the App, including, but not limited
            to, (i) product warranty or liability claims; (ii) any claim that
            the App fails to conform to any applicable legal or regulatory
            requirement; (iii) claims arising under consumer protection or
            similar legislation; or (iv) claims that the App infringes a third
            party’s intellectual property rights.
          </p>
          <p>
            In the event of any failure of the iOS version of the App to conform
            to any applicable warranty that has not been effectively disclaimed
            by these Terms, you may notify Apple, and Apple will refund the
            purchase price for the App (if any) to you; to the maximum extent
            permitted by applicable law, Apple will have no other warranty
            obligation whatsoever with respect to the App, and, as between Apple
            and ATM.com, any other claims, losses, liabilities, damages, costs,
            or expenses attributable to any failure to conform to any warranty
            will be ATM.com’s responsibility, but only to the extent provided by
            these Terms and any limitations provided herein. Please read the
            entire Terms, as other sections of these Terms may limit ATM.com’s
            liability in this regard.
          </p>
          <p>
            Apple, and Apple’s subsidiaries, are third-party beneficiaries of
            these Terms. Upon your acceptance of the Terms, Apple will have the
            right (and will be deemed to have accepted the right) to enforce the
            Terms against you as a third-party beneficiary thereof.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            21.4 Additional Google Terms
          </h3>
          <p>
            You acknowledge that Google is not responsible for addressing any
            claims you have or any claims of any third party relating to the App
            or your possession and use of the App, including, but not limited
            to, (i) product warranty or liability claims; (ii) any claim that
            the App fails to conform to any applicable legal or regulatory
            requirement; (iii) claims arising under consumer protection or
            similar legislation; or (iv) claims that the App infringes a third
            party’s intellectual property rights.
          </p>
          <h2
            id="mandatory-waiver"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            22. MANDATORY ARBITRATION AND CLASS ACTION WAIVER
          </h2>
          <p className="font-bold">
            PLEASE READ THIS SECTION CAREFULLY. THIS SECTION 22 INCLUDING ALL
            SUBSECTIONS 22.1 THROUGH AND INCLUDING 22.15 BELOW. SECTION 22
            AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN
            COURT.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.1 Application
          </h3>
          <p>
            You and ATM.com agree that these Terms affect interstate commerce
            and that the Federal Arbitration Act, 9 U.S.C. § 1 et seq., (the
            “FAA”) governs the interpretation and enforcement of these
            arbitration provisions. This Section 22 (Mandatory Arbitration and
            Class Action Waiver), is intended to be interpreted broadly and
            governs any and all disputes between us including but not limited to
            claims arising out of or relating to any aspect of the relationship
            between us, whether based in contract, tort, statute, fraud,
            misrepresentation or any other legal theory; claims that arose
            before these Terms or any prior agreement (including, but not
            limited to, claims related to advertising); and claims that may
            arise after the termination of these Terms. The only disputes
            excluded from this broad prohibition are the litigation of certain
            intellectual property and small court claims, as provided below.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.2 Initial Dispute Resolution
          </h3>
          <p>
            Most disputes can be resolved without resorting to arbitration. If
            you have any dispute with us, you agree that before taking any
            formal action, you will contact us at{' '}
            <a href="mailto:support@atm.com" className="underline">
              support@atm.com
            </a>{' '}
            and provide a brief, written description of the dispute along with
            your contact information, including your email address and mobile
            phone number (if your dispute relates to an account). Except for
            intellectual property and small claims court claims, the parties
            agree to use their best efforts to settle any dispute, claim,
            question, or disagreement directly through consultation with
            ATM.com. Good faith negotiations shall be a condition precedent to
            either party initiating a lawsuit or arbitration.
          </p>
          <p>
            As part of this informal dispute resolution process, either party
            may request a telephonic “Informal Dispute Resolution Conference” to
            discuss the matter in good faith. If requested, both parties agree
            to participate in the conference, which shall be scheduled at a
            mutually agreeable time. If you are represented by counsel, your
            attorney may participate, but you must also personally attend the
            conference.
          </p>
          <p>
            To initiate the informal dispute resolution process, either party
            must send a written notice (“Notice”) to the other party. Your
            Notice to ATM.com should be sent by email to{' '}
            <a href="mailto:support@atm.com" className="underline">
              support@atm.com
            </a>{' '}
            and must include the following: (1) your full name; (2) your
            telephone number, mailing address, and email address associated with
            your account (if any); (3) the name and contact information of your
            counsel, if applicable; and (4) a description of the dispute.
            ATM.com may send its Notice to the email or mailing address we have
            on file for you. It is your responsibility to ensure that your
            contact information remains accurate and up to date. The informal
            dispute resolution process shall last for sixty (60) days from the
            date Notice is received, during which both parties will engage in
            good faith efforts to resolve the dispute. This 60-day period is a
            mandatory precondition to initiating arbitration or filing a claim
            in small claims court. During this time, the statute of limitations
            and any applicable filing deadlines shall be tolled.
          </p>
          <p>
            The Informal Dispute Resolution Conference shall be individualized.
            A separate conference must be held for each dispute, even if the
            same attorney or law firm represents multiple users in similar
            cases, unless all parties expressly agree otherwise.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.3 Binding Arbitration
          </h3>
          <p>
            If the parties do not reach an agreed-upon solution within a period
            of sixty (60) days from the time informal dispute resolution is
            initiated under the Initial Dispute Resolution provision above, then
            either party may initiate binding arbitration as the sole means to
            resolve claims, (except as provided in subsection 22.10 below)
            subject to the terms set forth below. Specifically, all claims
            arising out of or relating to these Terms (including the Terms’
            formation, performance, and breach), the parties’ relationship with
            each other, and/or your use of the Services shall be finally settled
            by binding arbitration administered by American Arbitration
            Association (“AAA”) in accordance with the AAA Consumer Arbitration
            Rules and Mediation Procedures in effect at the time the arbitration
            is initiated, excluding any rules or procedures governing or
            permitting class actions.{' '}
            <span className="font-bold">
              THE ARBITRATION WILL BE CONDUCTED IN IRVINE, CALIFORNIA, UNLESS
              YOU AND WE AGREE OTHERWISE. REGARDLESS OF WHERE THE ARBITRATION IS
              CONDUCTED, YOU MAY ATTEND BY VIDEO CONFERENCE CALL.
            </span>
          </p>
          <p>
            If the AAA is unable or unwilling to administer a particular
            arbitration under this Section 22, the parties agree that JAMS shall
            serve as the alternative arbitration administrator, and the
            arbitration shall be conducted under the JAMS Streamlined
            Arbitration Procedure Rules for claims that do not exceed $250,000
            and the JAMS Comprehensive Arbitration Rules and Procedures for
            claims exceeding $250,000, in each case in effect at the time the
            arbitration is initiated and excluding any rules or procedures
            governing or permitting class actions. If neither the AAA nor JAMS
            is able or willing to administer the arbitration, the parties shall
            confer in good faith to agree on an alternative nationally
            recognized arbitration administrator that maintains consumer
            arbitration rules consistent with due process standards. If the
            parties are unable to agree on an alternative administrator within
            thirty (30) days, either party may petition a court of competent
            jurisdiction as identified in Section 22.10 to appoint an arbitrator
            or arbitration administrator. The inability or refusal of any
            particular arbitration administrator to administer the arbitration
            shall not be construed as a waiver of the parties’ agreement to
            arbitrate or as grounds for voiding this Section 22.
          </p>
          <p>
            ATM.com shall register its arbitration clause with the AAA Consumer
            Clause Registry and maintain such registration in good standing,
            including payment of all applicable registration and review fees.
            ATM.com’s failure to maintain its clause registration with the AAA
            shall not void this Section 22, but shall entitle you to elect to
            proceed under the fallback provider provisions described above.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.4 Arbitrator’s Powers and Delegation of Arbitrability
          </h3>
          <p>
            <span className="block underline">
              A. Delegation of Gateway Questions
            </span>
            The parties expressly agree that the arbitrator, and not any
            federal, state, or local court or agency, shall have sole and
            exclusive authority to resolve all disputes arising out of or
            relating to the interpretation, applicability, enforceability, or
            formation of this Section 22, including but not limited to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Whether a particular claim or dispute is subject to arbitration
              under this Section 22
            </li>
            <li>
              Whether this Section 22, or any part of it, is valid, enforceable,
              or void — including any challenge based on unconscionability,
              illegality, fraud in the inducement, or any other contract defense
              — whether such challenge is directed at this Section 22
              specifically or at the Terms of Service as a whole
            </li>
            <li>
              Whether the conditions precedent to arbitration (including the
              informal dispute resolution process in Section 22.2) have been
              satisfied
            </li>
            <li>
              Whether any party has waived its right to arbitrate by virtue of
              litigation conduct or otherwise
            </li>
            <li>
              Whether a dispute falls within the scope of any exception to
              arbitration set forth in this Section 22
            </li>
          </ul>
          <p>
            The parties expressly agree, consistent with Rent-A-Center, West,
            Inc. v. Jackson, 561 U.S. 63 (2010), and its progeny, that a
            challenge to the validity or enforceability of this arbitration
            agreement as a whole — as opposed to the Terms of Service as a whole
            — shall be decided by the arbitrator, not by any court. A party
            wishing to challenge the arbitrability of a dispute must raise that
            challenge before the arbitrator in the first instance.
          </p>
          <p>
            <span className="block underline">
              B. Arbitrator Qualifications
            </span>
            The arbitrator shall be either a retired state or federal court
            judge or an attorney who has been licensed to practice law for a
            minimum of ten (10) years, with experience in commercial disputes,
            consumer contracts, or technology law. The arbitrator shall be
            selected in accordance with the rules of the applicable AAA
            arbitration rules (or the rules of the applicable fallback
            administrator), subject to the qualification requirements set forth
            herein. Either party may object to a proposed arbitrator who does
            not meet these qualifications, and the administrator shall select an
            alternative arbitrator who does.
          </p>
          <p>
            <span className="block underline">
              C. Scope of Arbitrator's Remedial Authority
            </span>
            The arbitrator shall be empowered to grant any relief that would be
            available in a court of competent jurisdiction under applicable law
            or in equity, including monetary damages, declaratory relief, and
            injunctive relief, but solely on an individual basis and subject to
            the limitations set forth in this Section 22. The arbitrator shall
            have no authority to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Award relief on a class, collective, or representative basis
            </li>
            <li>
              Consolidate multiple arbitrations without the written consent of
              all parties
            </li>
            <li>
              Award punitive or exemplary damages except to the extent expressly
              authorized by applicable statute
            </li>
            <li>
              Modify, amend, or disregard any provision of this Section 22,
              except as expressly permitted by applicable law
            </li>
          </ul>
          <p>
            <span className="block underline">D. Form and Effect of Award</span>
            The arbitrator’s award shall be in writing, shall state the
            essential findings and conclusions upon which the award is based,
            and shall be binding on the parties. The award may be entered as a
            judgment in any court of competent jurisdiction. The arbitrator’s
            written decision shall be sufficient to support entry of judgment
            and shall be kept confidential in accordance with Section 22.14.
          </p>
          <p>
            <span className="block underline">
              E. Governing Law for Arbitrability
            </span>
            Notwithstanding any choice-of-law provision elsewhere in these
            Terms, the Federal Arbitration Act, 9 U.S.C. § 1 et seq.,
            exclusively governs the enforceability, interpretation, and
            administration of this Section 22 and preempts any contrary state
            law, including the California Arbitration Act, to the fullest extent
            permitted by law. Substantive California law shall otherwise apply
            to the underlying merits of any dispute, as provided in Section 25.
          </p>
          <p>
            <span className="block underline">
              F. Prohibition on Consolidation
            </span>
            No arbitration proceeding initiated under this Section 22 shall be
            consolidated with any other arbitration proceeding, whether pending
            or future, without the express prior written consent of all parties
            to each such proceeding. The arbitrator shall have no authority, sua
            sponte or upon motion of any party, to consolidate individual
            arbitrations or to conduct any form of joint or combined proceeding
            without such consent. This prohibition on consolidation is distinct
            from, and in addition to, the class action waiver set forth in
            Section 22.8, and applies even where multiple arbitrations involve
            similar or identical claims, parties, or legal issues. Any purported
            consolidation made without the written consent of all affected
            parties shall be void and of no force or effect.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.5 Filing a Demand
          </h3>
          <p>
            To initiate an arbitration, you must prepare a written Demand for
            Arbitration (the “Demand”) that includes:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Your full name, telephone number, mailing address, and email
              address, including the email associated with your ATM.com account
              (if applicable)
            </li>
            <li>
              A statement of the legal claims being asserted and the factual
              bases for those claims
            </li>
            <li>
              A description of the remedy sought and an accurate, good-faith
              calculation of the amount in controversy in U.S. Dollars
            </li>
            <li>
              A statement certifying completion of the informal dispute
              resolution process described above
            </li>
            <li>
              A statement certifying that you will pay any necessary filing fees
              in connection with the arbitration, subject to ATM.com’s
              obligations as described below
            </li>
          </ul>
          <p>
            If you are represented by counsel, the Demand must also include your
            counsel’s name, telephone number, mailing address, and email
            address. Your attorney must also sign the Demand and, by doing so,
            certify that to the best of their knowledge, information, and
            belief, formed after a reasonable inquiry, the Demand complies with
            standards similar to those in Federal Rule of Civil Procedure 11(b).
          </p>
          <p>
            You must send the Demand, along with the appropriate filing fee, to
            the AAA in accordance with the AAA Consumer Arbitration Rules and
            Mediation Procedures then in effect. The AAA’s filing procedures,
            including electronic filing options, are available at{' '}
            <a href="https://www.adr.org" target="_blank" className="underline">
              www.adr.org
            </a>
            .
          </p>
          <p>
            You must also send one copy of the Demand to ATM.com at: ATM.com,
            Inc., P.O. Box 7475, Attn: Legal Dept., Newport Beach, CA
            92658-7475.
          </p>
          <p>
            To the extent the filing fee exceeds the cost of filing a lawsuit in
            court, ATM.com will pay the difference. If the arbitrator finds the
            arbitration to be non-frivolous, ATM.com will pay all fees invoiced
            by the AAA (or applicable fallback administrator), including filing
            fees, arbitrator fees, and hearing expenses. Each party will be
            responsible for their own attorney’s fees unless applicable law or
            the arbitration rules provide otherwise.
          </p>
          <p>
            If you prevail on the merits of any claim in arbitration, ATM.com
            will reimburse you for any filing fee you paid in connection with
            the arbitration, in addition to any other relief awarded by the
            arbitrator. ATM.com commits to paying all fees and costs required of
            it by the applicable arbitration administrator within the time
            periods required by the administrator’s rules and applicable law,
            including but not limited to the requirements of California Code of
            Civil Procedure Section 1281.97 et seq. ATM.com’s failure to timely
            pay required fees shall be governed by applicable law and the rules
            of the applicable arbitration administrator.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.6 Discovery in Arbitration
          </h3>
          <p>
            <span className="block underline">
              A. Acknowledgment Regarding Arbitration vs. Litigation
            </span>
            The parties understand that, absent this mandatory arbitration
            provision, they would have the right to bring claims in court and
            have them heard by a judge or jury. The parties further acknowledge
            that arbitration may, in some cases, result in different costs than
            litigation, and that discovery rights in arbitration are more
            limited than in court proceedings. The discovery limitations set
            forth in this Section 22.6 are intended to reduce the cost and
            duration of arbitration while still affording each party a
            meaningful opportunity to develop and present its case.
          </p>
          <p>
            <span className="block underline">
              B. Default Discovery Limitations
            </span>
            Unless the parties mutually agree in writing to additional
            discovery, or unless the arbitrator orders expanded discovery
            pursuant to Section 22.6(D) below, discovery in any arbitration
            proceeding under this Section 22 shall be limited as follows:
          </p>
          <p>(i) Claims Up to $75,000 (Streamlined Track)</p>
          <p>
            For arbitrations in which the total amount in controversy does not
            exceed Seventy-Five Thousand Dollars ($75,000.00), discovery shall
            be limited to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Depositions: One (1) deposition per side, not to exceed four (4)
              hours in duration, conducted by video conference unless the
              parties agree otherwise or the arbitrator orders otherwise for
              good cause
            </li>
            <li>
              Document Requests: Twenty (20) document requests per side,
              including subparts, with responses due within thirty (30) days of
              service
            </li>
            <li>
              Interrogatories: Ten (10) interrogatories per side, including
              subparts, with responses due within thirty (30) days of service
            </li>
            <li>
              No requests for admission unless the arbitrator authorizes them
              upon a showing of good cause
            </li>
          </ul>
          <p>(ii) Claims Exceeding $75,000 (Standard Track)</p>
          <p>
            For arbitrations in which the total amount in controversy exceeds
            Seventy-Five Thousand Dollars ($75,000.00), discovery shall be
            limited to:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Depositions: Three (3) depositions per side, not to exceed seven
              (7) hours each, conducted in person or by video conference at the
              election of the deposing party
            </li>
            <li>
              Document Requests: Thirty-five (35) document requests per side,
              including subparts, with responses due within thirty (30) days of
              service
            </li>
            <li>
              Interrogatories: Fifteen (15) interrogatories per side, including
              subparts, with responses due within thirty (30) days of service
            </li>
            <li>
              Requests for Admission: Twenty (20) requests for admission per
              side, with responses due within thirty (30) days of service
            </li>
          </ul>
          <p>
            <span className="block underline">
              C. Mandatory Initial Disclosures
            </span>
            Within twenty-one (21) days of the filing of the respondent's
            answer, each party shall provide to the other party, without
            awaiting a discovery request, the following initial disclosures:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The name, address, and telephone number of each individual likely
              to have discoverable information that the disclosing party may use
              to support its claims or defenses
            </li>
            <li>
              A description by category and location of all documents,
              electronically stored information, and tangible items that the
              disclosing party has in its possession, custody, or control and
              may use to support its claims or defenses
            </li>
            <li>
              A computation of each category of damages claimed by the
              disclosing party, together with the documents or other evidentiary
              material on which each computation is based
            </li>
            <li>
              Copies of, or a description by category and location of, all
              documents and electronically stored information that the
              disclosing party has in its possession, custody, or control and
              may use to support its claims or defenses
            </li>
          </ul>
          <p>
            Mandatory initial disclosures shall not count against any party's
            document request limit.
          </p>
          <p>
            <span className="block underline">
              D. Expanded Discovery for Good Cause
            </span>
            Either party may apply to the arbitrator for leave to conduct
            discovery beyond the limits set forth in Section 22.6(B). The
            arbitrator may grant such leave upon a showing of good cause, which
            shall require the requesting party to demonstrate that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The additional discovery is necessary to allow a fair presentation
              of the requesting party's claims or defenses and cannot be
              obtained through the discovery already permitted
            </li>
            <li>
              The burden and expense of the additional discovery is
              proportionate to the amount in controversy and the importance of
              the issues at stake
            </li>
            <li>
              The additional discovery will not cause undue delay or prejudice
              to the other party
            </li>
          </ul>
          <p>
            Any application for expanded discovery must be made before the close
            of the standard discovery period and must be accompanied by a
            description of the specific additional discovery sought and the
            reasons why it is necessary. The arbitrator’s decision on any such
            application shall be final and not subject to further review.
          </p>
          <p>
            <span className="block underline">
              E. Electronically Stored Information
            </span>
            The parties agree to cooperate in good faith to limit the scope and
            cost of discovery of electronically stored information ("ESI").
            Unless the parties agree otherwise or the arbitrator orders
            otherwise, ESI discovery shall be limited to a single agreed-upon
            custodian per party for the Streamlined Track and up to three
            custodians per party for the Standard Track, and shall be limited to
            documents from the two (2) year period preceding the events giving
            rise to the claim unless a longer period is necessary to establish
            the claim or defense. The parties shall meet and confer regarding
            ESI search terms, custodians, and format of production within
            fourteen (14) days of the commencement of the discovery period. ESI
            document requests are subject to the document request limits in
            Section 22.6(B) and shall not be treated as a separate category of
            unlimited discovery.
          </p>
          <p>
            <span className="block underline">
              F. Confidentiality of Discovery Materials
            </span>
            All documents, testimony, and other materials produced or exchanged
            in discovery shall be treated as confidential in accordance with
            Section 22.14 of these Terms and may be used only for purposes of
            the arbitration proceeding in which they are produced. Neither party
            shall use discovery materials obtained in one arbitration proceeding
            in any other proceeding without the written consent of the producing
            party or an order of the arbitrator.
          </p>
          <p>
            <span className="block underline">G. Discovery Disputes</span>
            All discovery disputes shall be submitted to the arbitrator for
            resolution. The arbitrator shall have authority to issue orders
            compelling discovery, imposing sanctions for discovery abuse, and
            awarding reasonable costs and attorneys’ fees incurred in connection
            with discovery disputes where a party’s conduct in connection with
            discovery is found to have been unreasonable, dilatory, or made in
            bad faith. The arbitrator’s rulings on discovery disputes shall be
            final and binding.
          </p>
          <p>
            <span className="block underline">
              H. Preservation of Claims and Defenses
            </span>
            Nothing in this Section 22.6 shall be construed to prevent either
            party from presenting all legally relevant evidence at the
            arbitration hearing itself, including documents, witness testimony,
            and expert testimony, subject to the arbitrator’s authority to
            manage the hearing in a manner that is efficient and proportionate
            to the claims at issue. Discovery limitations in this Section 22.6
            apply to pre-hearing discovery only and do not limit what may be
            presented at the hearing.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.7 Attorney Fees, Costs, and Sanctions
          </h3>
          <p>
            <span className="block underline">A. General Rule</span>
            Each party shall bear its own attorneys’ fees and costs in
            connection with any arbitration proceeding under this Section 22,
            except as otherwise provided in this Section 22.7 or as required by
            applicable law.
          </p>
          <p>
            <span className="block underline">
              B. Mandatory Fee-Shifting for Frivolous or Improper Claims
            </span>
            If, after providing both parties a full and fair opportunity to be
            heard, the arbitrator determines that any claim, defense, or relief
            sought by a party fails to meet the standards set forth in Federal
            Rule of Civil Procedure 11(b) — meaning it was (a) not warranted by
            existing law or a non-frivolous argument for the extension,
            modification, or reversal of existing law; (b) not supported by
            evidence or reasonably likely to be supported by evidence after a
            reasonable opportunity for further investigation or discovery; or
            (c) presented for any improper purpose, including to harass, cause
            unnecessary delay, or needlessly increase the cost of dispute
            resolution — then the arbitrator shall (not may) award the
            prevailing party:
          </p>
          <ul className="list-disc pl-6">
            <li>
              All arbitration-related filing fees and administrative fees paid
              or incurred
            </li>
            <li>All arbitrator compensation and expenses</li>
            <li>
              All reasonable attorneys' fees and costs incurred in defending
              against the frivolous or improper claim or defense
            </li>
            <li>
              Any other costs or expenses the arbitrator determines to be
              reasonable and appropriate under the circumstances
            </li>
          </ul>
          <p>
            <span className="block underline">
              C. Personal Liability of Counsel
            </span>
            Where a party is represented by counsel, and the arbitrator
            determines that a claim, demand, or defense was filed or maintained
            in violation of the standards described in Section 22.7(B), the
            arbitrator shall have authority to assess fees, costs, and sanctions
            directly and personally against the offending party’s attorney or
            law firm, jointly and severally with the party, to the extent
            permitted by applicable law and the applicable arbitration rules. By
            submitting a Demand for Arbitration or any responsive filing signed
            by counsel, the attorney of record personally certifies that the
            filing meets the standards of Federal Rule of Civil Procedure 11(b)
            and agrees to be subject to this provision.
          </p>
          <p>
            <span className="block underline">
              D. Statutory Fee Entitlements
            </span>
            Nothing in this Section 22.7 shall be construed to limit any party’s
            right to recover attorneys’ fees or costs to which they are entitled
            under applicable federal or state statute, including but not limited
            to any consumer protection statute that independently provides for
            fee recovery by a prevailing claimant.
          </p>
          <p>
            <span className="block underline">E. Fee Motion Practice</span>
            Any motion for fees and costs under this Section 22.7 must be filed
            with the arbitrator within thirty (30) days of the issuance of the
            final arbitration award. The arbitrator shall retain jurisdiction to
            resolve fee disputes following issuance of the final award.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.8 Class Action Waiver
          </h3>
          <p>
            <span className="block underline">
              A. Class and Representative Action Waiver
            </span>
            <span className="font-bold">
              YOU AND ATM.COM AGREE THAT EACH PARTY MAY BRING CLAIMS AGAINST THE
              OTHER ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF,
              CLASS MEMBER, OR REPRESENTATIVE IN ANY PURPORTED CLASS ACTION,
              COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION (EXCEPT AS
              PROVIDED IN SECTION 22.8(C) BELOW), OR OTHER REPRESENTATIVE
              PROCEEDING.
            </span>{' '}
            The arbitrator shall have no authority to combine or aggregate
            similar claims, conduct any class proceeding, or make an award to
            any person or entity not a party to the individual arbitration. No
            arbitration conducted under these Terms shall be consolidated with
            any other arbitration without the prior written consent of all
            parties to each such arbitration. This waiver applies to all forms
            of representative or collective relief, whether sought in
            arbitration or in court.
          </p>
          <p>
            <span className="block underline">
              B. Severability of Class Action Waiver
            </span>
            If any court or arbitrator determines that any portion of the class
            action waiver set forth in Section 22.8(A) is void or unenforceable
            for any reason, then that specific portion shall be severed from
            this Agreement, and the remainder of the class action waiver and all
            arbitration provisions set forth in this Section 22 shall remain in
            full force and effect. Notwithstanding the foregoing, if a court or
            arbitrator determines that the class action waiver is unenforceable
            in a manner that would permit arbitration to proceed on a class or
            representative basis, then the arbitration provisions of this
            Section 22 shall be deemed null and void solely with respect to that
            class or representative claim, and such claim shall be litigated in
            a court of competent jurisdiction as set forth in Section 22.10. All
            individual claims shall remain subject to binding arbitration under
            this Section 22.
          </p>
          <p>
            <span className="block underline">
              C. California Private Attorneys General Act (PAGA)
            </span>
            To the maximum extent permitted by applicable law, you agree to
            waive any right to bring or participate in any representative claim
            under the California Private Attorneys General Act (“PAGA”), Cal.
            Lab. Code § 2698 et seq., or any similar state statute, in
            arbitration or in court. You and ATM.com agree that any claim you
            bring under PAGA shall be brought solely in your individual capacity
            on behalf of yourself and not on behalf of other current or former
            employees or aggrieved persons. Any individual PAGA claim shall be
            resolved through binding individual arbitration pursuant to this
            Section 22. Any non-individual or representative PAGA claims —
            meaning claims brought on behalf of other aggrieved persons — are
            waived to the maximum extent permitted by law. If, and only if, a
            court of competent jurisdiction determines that a non-individual
            PAGA claim may not be waived, such non-individual PAGA claim shall
            be stayed in court pending the final resolution of all individual
            arbitrable claims under this Section 22. ATM.com expressly reserves
            all rights and defenses with respect to any such non-individual PAGA
            claims.
          </p>
          <p>
            <span className="block underline">
              D. Mass Arbitration Protocol
            </span>
            (i) Definition. A “Mass Filing” occurs when twenty-five (25) or more
            substantially similar demands for arbitration are filed against
            ATM.com within any ninety (90) day period by the same attorney, law
            firm, or group of coordinated attorneys or law firms, regardless of
            whether the demands are filed simultaneously or in a coordinated
            series.
          </p>
          <p>
            (ii) Attorney-Client Certification Requirement. In the event of a
            Mass Filing, counsel for each claimant must, at the time of filing
            or within fourteen (14) days of ATM.com’s written request, certify
            in writing that: (a) counsel has an actual, individual
            attorney-client relationship with the named claimant and has
            communicated directly and individually with that claimant regarding
            the specific factual and legal basis of the claimant’s individual
            claim; (b) counsel has conducted a reasonable, individualized
            factual investigation of each claimant’s claim and has determined in
            good faith that the claim has merit; (c) the claimant has personally
            reviewed and authorized the filing of the arbitration demand on his
            or her behalf; and (d) the demand is not based solely on the
            claimant’s status as a user of the Services but reflects an
            individualized grievance. Failure to provide such certification upon
            request may result in dismissal of the demand without prejudice, and
            the arbitrator shall have authority to strike demands that lack the
            required certification.
          </p>
          <p>
            (iii) Bellwether Process. In the event of a Mass Filing, the parties
            agree that the following bellwether process shall apply in lieu of
            simultaneous individual arbitrations:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The AAA (or applicable administrator) shall randomly select ten
              (10) cases from the Mass Filing to proceed as bellwether
              arbitrations (the “Bellwether Cases”). All remaining cases shall
              be stayed pending resolution of the Bellwether Cases.
            </li>
            <li>
              The Bellwether Cases shall be arbitrated individually, one at a
              time, under the arbitration procedures set forth in this Section
              22. No Bellwether Case shall be consolidated with any other case
              without written consent of all parties.
            </li>
            <li>
              Upon the conclusion of the Bellwether Cases, the parties shall
              engage in a global mediation of all remaining stayed cases before
              a AAA mediator mutually agreed upon by the parties.
            </li>
            <li>
              If global mediation does not resolve the remaining cases within
              sixty (60) days of the conclusion of mediation, the stayed cases
              shall resume in arbitration in groups of ten (10), continuing
              until all cases are resolved or settled.
            </li>
          </ul>
          <p>
            (iv) Procedural Safeguards for Stayed Claimants. The bellwether
            process set forth in Section 22.8(D)(iii) is intended solely as a
            case management mechanism and shall be implemented consistent with
            the following procedural safeguards designed to protect the rights
            of all claimants:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Notice. All claimants whose cases are stayed shall receive written
              notice from the administrator identifying which cases have been
              selected as Bellwether Cases, the anticipated timeline for
              resolution, and the claimant’s continued rights under this Section
              22.
            </li>
            <li>
              Non-Binding Precedent. The outcome of any Bellwether Case shall
              not be binding precedent on, and shall not have res judicata,
              collateral estoppel, or any other preclusive effect on, any
              non-bellwether claimant. Each stayed claimant retains all
              individual claims, rights, and defenses that existed at the time
              the demand was filed.
            </li>
            <li>
              Time Limitation on Stays. No individual claimant’s case shall
              remain stayed for more than twenty-four (24) months from the date
              the stay was imposed. If a claimant’s case has been stayed for
              twenty-four (24) months and has not been activated for individual
              arbitration or resolved through mediation or settlement, that
              claimant’s case shall be activated for individual arbitration
              under this Section 22 upon written request by the claimant or the
              claimant’s counsel.
            </li>
            <li>
              Tolling. The statute of limitations and any other applicable
              filing deadlines shall be tolled for all stayed claimants from the
              date the stay is imposed until the date the stay is lifted or the
              claimant’s case is activated for individual arbitration.
            </li>
          </ul>
          <p>
            (v) Fee Allocation in Mass Filings. In the event of a Mass Filing,
            ATM.com’s obligation to pay arbitration fees shall be limited to the
            Bellwether Cases and each subsequent group of ten (10) cases as they
            are activated for arbitration. Filing fees for stayed cases shall
            not be assessed until those cases are activated. If the arbitrator
            or the AAA determines that a Mass Filing constitutes an abuse of the
            arbitration process or was filed in bad faith, the arbitrator may
            award ATM.com its reasonable attorneys’ fees, administrative fees,
            and arbitrator fees incurred in connection with the Mass Filing.
          </p>
          <p>
            (vi) Escrow for Mass Arbitration Fees. In the event of a Mass
            Filing, ATM.com may elect to deposit the arbitration fees required
            for the activated Bellwether Cases and each subsequent group of
            activated cases into a separate trust or escrow account maintained
            by a qualified escrow agent or the arbitration administrator. Funds
            shall be released from escrow to the administrator as each group of
            cases is set for hearing. This provision is intended to ensure that
            funds are available for the orderly administration of the
            arbitration while avoiding the imposition of fees for cases that may
            never proceed to hearing. The establishment of an escrow account
            shall not constitute a failure or refusal to pay arbitration fees
            under applicable law or the administrator’s rules, provided that
            fees for activated cases are disbursed from escrow in a timely
            manner consistent with the administrator’s payment deadlines.
          </p>
          <p>
            (vii) AAA Mass Arbitration Rules. In the event of a Mass Filing, the
            AAA’s Mass Arbitration Supplementary Rules, as then in effect, shall
            apply to the extent they are consistent with this Section 22.8(D).
            In the event of any conflict between the AAA Mass Arbitration
            Supplementary Rules and this Section 22.8(D), this Section 22.8(D)
            shall control.
          </p>
          <p>
            (viii) Consolidation Prohibition. Nothing in this Section 22.8(D)
            shall be construed to authorize or permit the consolidation of
            individual arbitrations into a single proceeding, the arbitration of
            class claims, or any other form of representative or collective
            arbitration. The bellwether process set forth above is intended
            solely as a case management mechanism and shall not alter each
            claimant’s individual arbitration rights or ATM.com’s individual
            defenses with respect to each claim.
          </p>
          <p>
            <span className="block underline">
              E. Waiver of Jury Trial on Representative Claims
            </span>
            <span className="font-bold">
              TO THE EXTENT ANY CLAIM IS DETERMINED TO BE INELIGIBLE FOR
              ARBITRATION AND IS PERMITTED TO PROCEED IN COURT, YOU AND ATM.COM
              EXPRESSLY WAIVE THE RIGHT TO A JURY TRIAL ON THAT CLAIM TO THE
              FULLEST EXTENT PERMITTED BY APPLICABLE LAW. SUCH CLAIMS SHALL BE
              RESOLVED BY A JUDGE SITTING WITHOUT A JURY.
            </span>
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.9 Waiver of Jury Trial
          </h3>
          <p>
            <span className="font-bold">
              YOU AND ATM.COM HEREBY WAIVE ANY CONSTITUTIONAL OR STATUTORY RIGHT
              TO SUE IN COURT AND HAVE A TRIAL BEFORE A JUDGE OR JURY.
            </span>{' '}
            Instead, all disputes shall be resolved through arbitration, as
            provided in this Arbitration Agreement. There is no judge or jury in
            arbitration, and judicial review of an arbitration award is limited.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.10 Exceptions: Intellectual Property Claims; Unenforceable
            Awards; Small Claims Court
          </h3>
          <p>
            NOTWITHSTANDING THE PARTIES’ AGREEMENT TO RESOLVE ALL DISPUTES
            THROUGH ARBITRATION, EITHER PARTY MAY BRING THE FOLLOWING CLAIMS IN
            COURT WITHOUT FIRST ENGAGING IN THE INFORMAL DISPUTE RESOLUTION
            PROCESS SET FORTH IN SECTION 22.2 OR THE ARBITRATION PROCESS SET
            FORTH IN SECTION 22.3:
          </p>
          <p>
            (a) Intellectual Property Claims. Either party may bring enforcement
            actions, validity determinations, or claims arising from or relating
            to theft, piracy, or unauthorized use of intellectual property —
            including but not limited to patents, copyrights, trademarks, trade
            secrets, and domain names — exclusively in the state courts of
            California located in Orange County, California, or in the United
            States District Court for the Central District of California. Each
            party hereby submits to the personal jurisdiction and venue of such
            courts and irrevocably waives any objection thereto, including any
            objection based on improper venue or forum non conveniens.
          </p>
          <p>
            (b) Enforcement of Unenforceable Arbitration Award. If an
            arbitration award is found by a court to be unenforceable for any
            reason, either party may pursue the underlying claim in the state
            courts of California located in Orange County, California, or in the
            United States District Court for the Central District of California,
            subject to all other limitations and waivers set forth in this
            Section 22, including the class action waiver set forth in Section
            22.8 and the jury trial waiver set forth in Section 22.9, to the
            extent such waivers are independently enforceable.
          </p>
          <p>
            (c) Small Claims Court. Either party may bring an individual claim
            in small claims court in lieu of arbitration, provided that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The claim falls within the jurisdictional limits of the applicable
              small claims court — currently a maximum of Twelve Thousand Five
              Hundred Dollars ($12,500.00) for most claims in California courts,
              subject to change by applicable law
            </li>
            <li>
              The claim is brought solely in an individual capacity and not on a
              class, collective, or representative basis
            </li>
            <li>
              The claim remains within the small claims court's jurisdiction
              throughout the pendency of the proceeding
            </li>
            <li>
              If the claim is removed from small claims court for any reason, or
              if the amount in controversy is determined to exceed the
              applicable jurisdictional limit, the claim shall be resolved by
              binding arbitration under this Section 22
            </li>
          </ul>
          <p>
            The small claims court exception is available only for matters that
            fall squarely within the jurisdictional scope of the applicable
            small claims court. Any attempt to artificially reduce the amount of
            a claim solely for the purpose of qualifying for small claims court
            jurisdiction may be treated by ATM.com as a violation of the good
            faith obligations set forth in Section 22.2.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.11 Limited Right to Opt Out
          </h3>
          <p>
            <span className="block underline">
              A. Opt-Out Eligibility and Deadline
            </span>
            If you are a new user who has not previously agreed to an
            arbitration provision with ATM.com, you may opt out of the
            arbitration and class action waiver provisions set forth in this
            Section 22 by submitting a valid Opt-Out Notice (as defined below)
            within thirty (30) days of the date you first create an account with
            ATM.com or otherwise first become subject to this Agreement,
            whichever is earlier (the “Opt-Out Deadline”). Opt-out requests
            submitted after the Opt-Out Deadline will not be valid and will have
            no effect. If you previously agreed to a version of this Agreement
            that contained an arbitration provision and did not validly opt out
            at that time, you are not eligible to opt out of this Section 22 and
            this Section 22.11 does not apply to you.
          </p>
          <p>
            <span className="block underline">
              B. How to Submit an Opt-Out Notice
            </span>
            To opt out, you must submit a written notice ("Opt-Out Notice")
            exclusively by one of the following two methods:
          </p>
          <p>
            <span className="block">
              By Certified U.S. Mail (postmarked on or before the Opt-Out
              Deadline):
            </span>
            <span className="block">ATM.com, Inc.</span>
            <span className="block">P.O. Box 7475</span>
            <span className="block">Newport Beach, CA 92658-7475</span>
            <span className="block">
              Attn: Legal Department — Arbitration Opt-Out
            </span>
          </p>
          <p>
            <span className="block">
              By Email (received on or before the Opt-Out Deadline):
            </span>
            <span className="block">
              <a className="underline" href="mailto:arbitration-optout@atm.com">
                arbitration-optout@atm.com
              </a>
            </span>
          </p>
          <p>
            To reduce the risk of your opt-out being missed or misdirected,
            opt-out notices sent by email must be sent exclusively to the
            dedicated email address above. Opt-out notices sent to any other
            ATM.com email address, including{' '}
            <a href="mailto:legal@atm.com" className="underline">
              legal@atm.com
            </a>{' '}
            or any general customer service address, will not be considered
            valid and will have no effect. ATM.com is not responsible for
            opt-out notices that are lost, misdirected, or otherwise fail to
            reach the designated address.
          </p>
          <p>
            <span className="block underline">
              C. Required Contents of the Opt-Out Notice
            </span>
            To be valid, your Opt-Out Notice must include all of the following:
          </p>
          <ul className="list-disc pl-6">
            <li>
              A clear and unambiguous statement of your intent to opt out of the
              arbitration agreement and class action waiver set forth in Section
              22 of the ATM.com Terms of Service
            </li>
            <li>
              The date you first created your ATM.com account or otherwise first
              became subject to this Agreement
            </li>
            <li>Your full legal name</li>
            <li>Your current mailing address</li>
            <li>The email address associated with your ATM.com account</li>
            <li>
              The mobile phone number associated with your ATM.com account (if
              applicable)
            </li>
            <li>
              Your personal signature (wet ink signature for mailed notices;
              typed name or electronic signature for emailed notices, provided
              the email is sent from the email address associated with your
              account)
            </li>
          </ul>
          <p>
            An Opt-Out Notice that is missing any of the above information, or
            that cannot be matched to an active ATM.com account, will be deemed
            invalid and will have no effect. ATM.com reserves the right to
            request additional verification of your identity before processing
            an opt-out.
          </p>
          <p>
            <span className="block underline">
              D. Personal Submission Required
            </span>
            You must submit the Opt-Out Notice yourself, in your individual
            capacity. Opt-Out Notices submitted by third parties on your behalf
            — including but not limited to attorneys, legal representatives,
            claims aggregators, or any other persons or entities — will not be
            valid and will have no effect. Mass opt-out submissions (meaning
            opt-out notices submitted on behalf of multiple individuals in a
            single submission or through a coordinated process) will not be
            accepted and will have no effect as to any individual purportedly
            included in such submission. Each individual wishing to opt out must
            submit a separate, individually signed Opt-Out Notice.
          </p>
          <p>
            <span className="block underline">E. ATM.com Confirmation</span>
            Upon receipt of a valid Opt-Out Notice, ATM.com will send written
            confirmation to the email address associated with your account
            within fifteen (15) business days. If you do not receive a
            confirmation within that period, it is your responsibility to
            contact ATM.com’s Legal Department at the dedicated opt-out email
            address above to confirm receipt. The absence of a confirmation does
            not itself invalidate a timely and properly submitted Opt-Out
            Notice, but ATM.com’s records regarding receipt shall be
            presumptively correct absent clear and convincing evidence to the
            contrary.
          </p>
          <p>
            <span className="block underline">F. Effect of Opting Out</span>
            If you submit a valid and timely Opt-Out Notice, neither you nor
            ATM.com will be bound by the arbitration or class action waiver
            provisions set forth in this Section 22 with respect to disputes
            arising after the date your opt-out is confirmed. However:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Opting out of this Section 22 does not affect any other provision
              of the Terms of Service, all of which remain in full force and
              effect
            </li>
            <li>
              Opting out of this Section 22 does not affect any prior
              arbitration agreements between you and ATM.com, which shall remain
              governed by the terms in effect at the time they were entered into
            </li>
            <li>
              If you opt out, any disputes between you and ATM.com that are not
              resolved informally shall be subject to the exclusive jurisdiction
              and venue provisions set forth in Section 22.10, and you hereby
              consent to personal jurisdiction in those courts
            </li>
          </ul>
          <p>
            <span className="block underline">
              G. Class Action Waiver Opt-Out
            </span>
            Opting out of the arbitration provision does not automatically opt
            you out of the class action waiver set forth in Section 22.8 to the
            extent such waiver is enforceable independent of the arbitration
            agreement under applicable law. If you wish to opt out of both the
            arbitration provision and the class action waiver, you must
            expressly state both in your Opt-Out Notice.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.12 Modifications to This Section
          </h3>
          <p>
            <span className="block underline">
              A. ATM.com's Right to Modify
            </span>
            ATM.com reserves the right to modify this Section 22 (Mandatory
            Arbitration and Class Action Waiver) at any time in its sole
            discretion. Any modifications will become effective as provided in
            this Section 22.12.
          </p>
          <p>
            <span className="block underline">
              B. Notice of Material Changes
            </span>
            If ATM.com makes a change to this Section 22 that materially affects
            your rights (a “Material Change”), ATM.com will provide notice by
            one or more of the following methods, as determined by ATM.com in
            its reasonable discretion:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Posting the updated Terms of Service on the ATM.com website with a
              revised "Last Updated" date prominently displayed at the top of
              the page
            </li>
            <li>
              Sending notice to the email address associated with your account
            </li>
            <li>
              Displaying a prominent in-app or in-platform notification the next
              time you access the Services
            </li>
          </ul>
          <p>
            For purposes of this Section 22.12, a “Material Change” means any
            modification that: (a) reduces your ability to bring claims in
            arbitration; (b) increases your cost of participating in
            arbitration; (c) changes the arbitration administrator or applicable
            rules in a manner that is materially less favorable to you; or (d)
            eliminates or narrows any exception to arbitration that previously
            applied to you. Modifications that are administrative in nature,
            that expand your rights, or that are required to maintain compliance
            with applicable law shall not constitute Material Changes and may
            take effect immediately upon posting.
          </p>
          <p>
            <span className="block underline">
              C. Affirmative Acceptance Required for Material Changes
            </span>
            For any Material Change to this Section 22, ATM.com will require
            your affirmative acceptance before the modified terms become binding
            on you. Affirmative acceptance may be obtained by:
          </p>
          <ul className="list-disc pl-6">
            <li>
              A click-through acknowledgment within the ATM.com platform or
              application requiring you to affirmatively accept the updated
              terms before continuing to use the Services
            </li>
            <li>
              A written confirmation sent by you to ATM.com's Legal Department
              acknowledging acceptance of the modified terms
            </li>
          </ul>
          <p>
            Continued use of the Services alone shall not constitute acceptance
            of a Material Change to this Section 22. If you do not affirmatively
            accept a Material Change within sixty (60) days of receiving notice,
            you may continue to use the Services but will remain subject to the
            version of Section 22 that was in effect immediately prior to the
            Material Change, until such time as you affirmatively accept the
            updated terms or terminate your use of the Services.
          </p>
          <p>
            <span className="block underline">D. Non-Material Changes</span>
            For modifications that do not constitute a Material Change, the
            updated terms will take effect thirty (30) days after posting on the
            ATM.com website. Your continued use of the Services after the
            effective date of any non-material modification constitutes your
            acceptance of the modified terms. It is your responsibility to
            review the Terms of Service periodically for updates. The “Last
            Updated” date at the top of the Terms of Service will reflect the
            date of the most recent modification.
          </p>
          <p>
            <span className="block underline">
              E. Disputes Accruing Before Modification
            </span>
            Any dispute, claim, or controversy that accrued before the effective
            date of a modification to this Section 22 shall be governed by the
            version of this Section 22 that was in effect at the time the
            dispute accrued, regardless of when the arbitration demand or other
            legal action is filed. For purposes of this provision, a dispute
            “accrues” on the date the events giving rise to the claim occurred,
            not the date on which a formal demand or filing is made.
          </p>
          <p>
            <span className="block underline">
              F. Previously Opted-Out Users
            </span>
            If you previously submitted a valid Opt-Out Notice under Section
            22.11 of any prior version of this Agreement, that opt-out remains
            valid and in effect. Modifications to this Section 22 — whether
            Material Changes or otherwise — will not retroactively subject you
            to arbitration if you previously and validly opted out, unless you
            separately and affirmatively agree in writing to be bound by the
            arbitration provisions of a subsequent version of this Agreement.
          </p>
          <p>
            <span className="block underline">
              G. Previously Agreed-Upon Arbitration
            </span>
            If you previously agreed to a version of this Agreement that
            contained an arbitration provision and did not validly opt out, any
            Material Change to this Section 22 will not give you a new
            opportunity to opt out of arbitration, except to the extent required
            by applicable law or the rules of the applicable arbitration
            administrator. You will, however, be entitled to the benefit of any
            modification that expands your rights or reduces your obligations
            under this Section 22.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.13 Survival
          </h3>
          <p>
            The provisions of Sections 25 (Governing Law) and 22 (Mandatory
            Arbitration and Class Action Waiver) shall survive any termination
            of your use of the Services.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.14 Confidentiality of Arbitration Proceedings
          </h3>
          <p>
            <span className="block underline">
              A. General Confidentiality Obligation
            </span>
            The parties agree that all aspects of any arbitration conducted
            under this Section 22 shall be and remain strictly confidential.
            This confidentiality obligation is mutual and applies equally to
            both you and ATM.com. Unless required by applicable law, court
            order, or the rules of the applicable arbitration administrator,
            neither party shall disclose to any third party, without the prior
            written consent of the other party:
          </p>
          <ul className="list-disc pl-6">
            <li>
              The existence of the arbitration or the fact that a demand has
              been filed
            </li>
            <li>
              The nature, substance, or content of any claims, defenses, or
              counterclaims asserted in the arbitration
            </li>
            <li>
              Any documents, evidence, testimony, or other materials produced or
              exchanged during the arbitration
            </li>
            <li>
              Any rulings, orders, decisions, or awards issued by the arbitrator
            </li>
            <li>
              Any settlement discussions, offers, or agreements reached in
              connection with the arbitration
            </li>
          </ul>
          <p>
            <span className="block underline">B. Permitted Disclosures</span>
            Notwithstanding Section 22.14(A), a party may disclose confidential
            arbitration information:
          </p>
          <ul className="list-disc pl-6">
            <li>
              To its attorneys, accountants, financial advisors, or other
              professional advisors who have a legitimate need to know and who
              are themselves bound by confidentiality obligations at least as
              protective as those set forth herein
            </li>
            <li>
              To the extent necessary to enforce an arbitration award in a court
              of competent jurisdiction, provided that the party seeks to file
              such materials under seal to the extent permitted by the court
            </li>
            <li>
              To the extent required by applicable securities laws or
              regulations, including disclosures required by the SEC or any
              stock exchange
            </li>
            <li>
              To the extent ordered by a court of competent jurisdiction,
              provided that the disclosing party gives the other party prompt
              written notice and a reasonable opportunity to seek a protective
              order before making any such disclosure
            </li>
            <li>
              To the extent the information is or becomes publicly available
              through no fault of the disclosing party.
            </li>
          </ul>
          <p>
            <span className="block underline">
              C. Confidentiality of Settlement
            </span>
            Any settlement reached in connection with an arbitration under this
            Section 22 shall be treated as confidential by both parties. Neither
            party shall disclose the existence, terms, or amount of any
            settlement to any third party without the prior written consent of
            the other party, except as required by applicable law or as
            permitted under Section 22.14(B) above.
          </p>
          <p>
            <span className="block underline">
              D. Breach of Confidentiality
            </span>
            The parties acknowledge that any breach of this Section 22.14 would
            cause irreparable harm for which monetary damages would be an
            inadequate remedy. In the event of a breach or threatened breach of
            this Section 22.14, the non-breaching party shall be entitled to
            seek injunctive or other equitable relief in any court of competent
            jurisdiction as identified in Section 22.10, without the requirement
            of posting a bond or other security, and without waiving its right
            to compel arbitration of the underlying dispute.
          </p>
          <p>
            <span className="block underline">E. Arbitrator's Authority</span>
            The arbitrator shall have authority to issue orders protecting the
            confidentiality of the proceedings and to impose sanctions,
            including monetary sanctions and adverse inference instructions, for
            violations of this Section 22.14 or of any confidentiality order
            issued during the arbitration.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            22.15 Hearing Format; Virtual Proceedings
          </h3>
          <p>
            <span className="block underline">A. Default Virtual Format</span>
            For all arbitrations in which the amount in controversy does not
            exceed Seventy-Five Thousand Dollars ($75,000.00), all hearings,
            conferences, and proceedings shall be conducted entirely by video
            conference or telephone, using a platform designated by the AAA (or
            applicable fallback administrator) or agreed upon by the parties,
            unless both parties expressly agree in writing to an in-person
            proceeding. ATM.com shall not request or require an in-person
            hearing for any matter within this threshold.
          </p>
          <p>
            <span className="block underline">B. Larger Claims</span>
            For arbitrations in which the amount in controversy exceeds
            Seventy-Five Thousand Dollars ($75,000.00), either party may request
            an in-person hearing by submitting a written request to the AAA (or
            applicable fallback administrator) and the other party. In-person
            hearings for such matters shall be conducted in Irvine, California,
            unless the parties mutually agree in writing to an alternative
            location or to conduct the hearing by video conference. The
            arbitrator shall have authority to order video conference hearings
            in lieu of in-person hearings upon a showing of good cause,
            including but not limited to hardship, cost, or health
            considerations.
          </p>
          <p>
            <span className="block underline">C. Technology and Access</span>
            ATM.com agrees to cooperate in good faith to ensure that virtual
            hearings are accessible and technically feasible for all parties. If
            a party lacks access to the technology required for a video
            conference hearing, the arbitrator may, in their discretion, allow
            telephonic participation or make other reasonable accommodations.
          </p>
          <p>
            <span className="block underline">D. No Adverse Inference</span>
            The arbitrator shall not draw any adverse inference against any
            party solely on the basis of that party’s participation in a hearing
            by video conference or telephone rather than in person.
          </p>
          <h2
            id="taxes"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            23. TAXES
          </h2>
          <p>
            You are responsible for any applicable taxes associated with income
            you generate via the Services. All payments from us to you in
            relation to the Services will be treated as inclusive of tax (when
            applicable) and will not be adjusted.
          </p>
          <p>
            In order to comply with all tax authorities, ATM.com reserves the
            right to disclose revenue information to said authorities when
            applicable. In addition, we may be required to send users a
            1099-MISC form or other applicable tax documents. The filing of tax
            forms is the sole responsibility of you, the end user. You agree to
            absolve ATM.com of responsibility for any related filing fees you
            may incur (for example, being charged a penalty for failing to file
            said tax forms in a timely manner).
          </p>
          <h2
            id="assignment"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            24. ASSIGNMENT
          </h2>
          <p>
            You may not transfer, assign, or delegate these Terms or your rights
            or obligations hereunder or your ATM.com Account in any way (by
            operation of law or otherwise) without our prior written consent. To
            the extent permitted by applicable law, we may transfer, assign, or
            delegate these Terms and our rights and obligations hereunder
            without your consent.
          </p>
          <h2
            id="governing-law"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            25. GOVERNING LAW
          </h2>
          <p>
            These Terms of Service will be governed by and construed in
            accordance with the laws of the State of California, without giving
            effect to any choice of law or conflict of law rules or provisions,
            whether Federal, or of any other jurisdiction, which would result in
            the application of the laws of a jurisdiction other than the State
            of California.
          </p>
          <p>
            Notwithstanding the foregoing, and notwithstanding any other
            provision of these Terms, the interpretation, enforceability, scope,
            formation, and administration of the arbitration agreement and class
            action waiver set forth in Section 22 of these Terms — including all
            questions of arbitrability, the validity of the arbitration
            agreement, the enforceability of the class action waiver, and the
            conduct of arbitration proceedings — shall be governed exclusively
            by the Federal Arbitration Act, 9 U.S.C. § 1 et seq. (the "FAA"),
            and not by the laws of California or any other state, to the fullest
            extent permitted by law. Where the FAA is silent on a procedural or
            substantive question arising under Section 22, the rules of the
            applicable arbitration administrator (currently AAA) shall apply,
            and California law shall apply only as a last resort and only to the
            extent it is not inconsistent with the FAA or the parties' agreement
            in Section 22. The California Arbitration Act, Cal. Civ. Proc. Code
            § 1280 et seq., shall have no application to Section 22 or to any
            dispute regarding the enforceability or administration of the
            arbitration agreement contained therein.
          </p>
          <h2
            id="other-terms"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            26. OTHER TERMS
          </h2>
          <p>
            These Terms of Service and any operating rules for the Services
            established by ATM.com constitute the entire agreement of the
            parties with respect to the subject matter hereof and supersede all
            previous written or oral agreements between the parties with respect
            to such subject matter. The provisions of these Terms of Service are
            for the benefit of ATM.com, its parent, subsidiaries, other
            affiliates and its third-party content providers and licensors and
            each shall have the right to assert and enforce such provisions
            directly or on its own behalf. You are also subject to United States
            export controls and are responsible for any violations of United
            States embargoes or other federal rules and regulations restricting
            exports. No waiver by either party of any breach or default
            hereunder shall be deemed to be a waiver of any preceding or
            subsequent breach or default. If any part of these Terms is found by
            a court of competent jurisdiction to be invalid or unenforceable, it
            will be replaced with language reflecting the original purpose in a
            valid and enforceable manner. The enforceable sections of these
            Terms of Service will remain binding upon the parties. The section
            headings used herein are for convenience only and shall not be given
            any legal import.
          </p>
          <p>
            Neither ATM.com nor you shall be liable for damages or for delays or
            failures in performance resulting from acts or occurrences beyond
            their reasonable control, including without limitation: fire,
            lightning, explosion, power surge or failure, flood, earthquake, or
            other natural disasters; acts of God; war, terrorism, revolution,
            civil commotion, riots, or acts of civil or military authorities or
            public enemies; any law, order, regulation, ordinance, or
            requirement of any government or legal body or any representative of
            any such government or legal body; pandemic, epidemic, or public
            health emergency declared by a federal, state, or local authority;
            labor unrest, including without limitation strikes, slowdowns,
            picketing, or boycotts; inability to secure raw materials,
            transportation facilities, fuel or energy shortages; cybersecurity
            incidents, distributed denial of service attacks, or other malicious
            interference with ATM.com's technical infrastructure beyond
            ATM.com's reasonable control; or acts or omissions of third-party
            service providers, telecommunications carriers, or internet service
            providers beyond ATM.com's reasonable control (each, a "Force
            Majeure Event").
          </p>
          <p>
            In the event of a Force Majeure Event, the affected party shall
            notify the other party as soon as reasonably practicable and shall
            use commercially reasonable efforts to resume performance as quickly
            as possible. Notwithstanding any Force Majeure Event, ATM.com's
            obligation to maintain the integrity of your Account balance and to
            process your withdrawal requests shall not be suspended or excused,
            except to the minimum extent technically impossible due to the Force
            Majeure Event itself and only for the duration of that technical
            impossibility. ATM.com will restore your ability to withdraw
            available Account funds as soon as reasonably practicable following
            the resolution or abatement of the Force Majeure Event. Nothing in
            this force majeure provision shall be construed to limit ATM.com's
            obligations under applicable law with respect to the safeguarding or
            return of user funds.
          </p>
          <h2
            id="privacy-policy"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            27. PRIVACY POLICY
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.1 Incorporation by Reference
          </h3>
          <p>
            ATM.com maintains a Privacy Policy that details how we collect, use,
            store, share, and protect your personal information in connection
            with the Services. Our Privacy Policy is incorporated into these
            Terms of Service by reference and forms a legally binding part of
            this Agreement. By agreeing to these Terms, you also agree to the
            Privacy Policy as it exists at the time of your agreement and as it
            may be amended from time to time in accordance with this Section 27.
            A current copy of our Privacy Policy is available at{' '}
            <Link href="/privacy-policy" target="_blank" className="underline">
              https://atm.com/privacy-policy
            </Link>{' '}
            and within the App.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.2 Modifications to the Privacy Policy
          </h3>
          <p>
            ATM.com reserves the right to update and modify the Privacy Policy
            from time to time at its discretion. However, not all Privacy Policy
            changes are treated the same way. The following rules apply
            depending on the nature of the change:
          </p>
          <p>
            (a) Non-Material Changes. Changes to the Privacy Policy that do not
            materially affect your rights or ATM.com's data collection, use, or
            sharing practices — such as clarifications, corrections of
            typographical errors, reorganization of existing content, or updates
            required to reflect changes in applicable law that do not expand
            ATM.com's data practices — will take effect thirty (30) days after
            posting on the ATM.com website and within the App, with the "Last
            Updated" date on the Privacy Policy reflecting the date of the most
            recent change. Your continued use of the Services after the
            effective date of a non-material change constitutes your acceptance
            of that change.
          </p>
          <p>
            (b) Material Changes. A "Material Change" to the Privacy Policy
            means any change that:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Expands the categories of personal information ATM.com collects
              from users
            </li>
            <li>
              Introduces new purposes for which ATM.com uses your personal
              information that are materially different from the purposes
              disclosed at the time you created your Account
            </li>
            <li>
              Adds new categories of third parties with whom ATM.com shares or
              sells your personal information, beyond those disclosed in Section
              7 of these Terms and in the Privacy Policy at the time of your
              agreement
            </li>
            <li>
              Reduces or eliminates any privacy right or protection previously
              afforded to you under the Privacy Policy
            </li>
            <li>
              Changes ATM.com's data retention practices in a manner that
              results in your personal information being retained for a
              materially longer period than previously disclosed
            </li>
          </ul>
          <p>
            For any Material Change to the Privacy Policy, ATM.com will provide
            you with advance notice of at least thirty (30) days before the
            change takes effect, by one or more of the following methods: email
            to the address associated with your Account; in-app notification; or
            a prominent notice on the ATM.com website. Your continued use of the
            Services after the effective date of a Material Change to the
            Privacy Policy constitutes your acceptance of that change. If you do
            not agree to a Material Change, you must stop using the Services
            before the effective date of the change.
          </p>
          <p>
            (c) Changes Required by Law. If a change to the Privacy Policy is
            required by applicable law or regulation — including but not limited
            to changes required to comply with the CCPA, CPRA, or other
            applicable privacy laws — ATM.com may implement such changes
            immediately upon posting, without advance notice, to the extent
            required by the applicable law or regulatory authority. ATM.com will
            notify you of such changes as promptly as practicable under the
            circumstances.
          </p>
          <p>
            (d) Changes Affecting the Arbitration Agreement. Notwithstanding
            anything in this Section 27 to the contrary, any change to the
            Privacy Policy that materially affects your rights under Section 22
            of these Terms (Mandatory Arbitration and Class Action Waiver) shall
            be treated as a Material Change to Section 22 and shall be governed
            by Section 22.12, not by this Section 27. Your continued use of the
            Services alone shall not constitute acceptance of any Privacy Policy
            change that constitutes a Material Change to Section 22.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.3 Conflicts Between the Privacy Policy and These Terms
          </h3>
          <p>
            In the event of any conflict or inconsistency between the Privacy
            Policy and these Terms of Service with respect to privacy-related
            matters, the Privacy Policy shall control. In the event of any
            conflict or inconsistency between the Privacy Policy and these Terms
            of Service with respect to any matter other than privacy — including
            but not limited to dispute resolution, arbitration, limitation of
            liability, and indemnification — these Terms of Service shall
            control. This conflict resolution provision is intended to ensure
            that each document governs the subject matter for which it was
            primarily designed.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.4 Third-Party Privacy Practices
          </h3>
          <p>
            The Privacy Policy governs only ATM.com's own data collection, use,
            and sharing practices. It does not govern the privacy practices of
            third parties whose services are integrated into or accessible
            through the Services, including but not limited to Plaid, Inc.,
            Cardlytics, Inc., third-party advertisers, and survey providers.
            Each such third party maintains its own privacy policy governing its
            independent data practices, and ATM.com is not responsible for those
            practices. Links to third-party privacy policies for ATM.com's
            primary service partners are available in the Privacy Policy. You
            are encouraged to review those policies before providing information
            to or through any third-party service.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.5 Data Breach Notification
          </h3>
          <p>
            In the event of a data breach affecting your personal information,
            ATM.com will notify you in accordance with applicable state and
            federal data breach notification laws, including California Civil
            Code Section 1798.29 and Section 1798.82. Notification will be
            provided to the email address associated with your Account, or by
            substitute notice as permitted by applicable law if ATM.com is
            unable to reach you at your email address. The timing and content of
            breach notifications will comply with applicable law. Nothing in
            this Section 27.5 shall be construed to limit ATM.com's notification
            obligations under applicable law or to expand them beyond what the
            law requires.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.6 Do Not Track
          </h3>
          <p>
            Some web browsers offer a "Do Not Track" or similar feature that
            signals to websites and applications that you do not wish to have
            your online activity tracked. [ATM.com's current practices with
            respect to Do Not Track signals are described in the Privacy
            Policy.] To the extent ATM.com's response to Do Not Track signals
            changes, ATM.com will update the Privacy Policy accordingly and
            provide notice of any Material Change in accordance with Section
            27.2(b) above.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.7 Children's Privacy
          </h3>
          <p>
            The Services are not directed to children under the age of 13, and
            ATM.com does not knowingly collect personal information from
            children under 13. If ATM.com becomes aware that it has
            inadvertently collected personal information from a child under 13,
            it will take prompt steps to delete that information from its
            records. If you are a parent or guardian and believe that ATM.com
            has collected personal information from your child under the age of
            13, please contact us immediately at{' '}
            <a href="mailto:privacy@atm.com" className="underline">
              privacy@atm.com
            </a>
            . This Section 27.7 is consistent with ATM.com's obligations under
            the Children's Online Privacy Protection Act ("COPPA"), 15 U.S.C. §
            6501 et seq.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            27.8 Your Privacy Responsibilities
          </h3>
          <p>
            While ATM.com takes reasonable steps to protect your personal
            information, you also bear responsibility for protecting your own
            privacy in connection with your use of the Services. Specifically:
          </p>
          <ul className="list-disc pl-6">
            <li>
              You are responsible for maintaining the confidentiality of your
              Account Credentials and for all activity that occurs under your
              Account, as described in Section 8.3 of these Terms.
            </li>
            <li>
              You should not share sensitive personal or financial information
              through the Services in ways that are not required for the
              functioning of the Services, including through chatbot or AI
              interactions as described in Section 12.
            </li>
            <li>
              You should review the privacy settings available within the App
              periodically and configure them in accordance with your
              preferences.
            </li>
            <li>
              If you believe your personal information has been compromised, you
              should contact ATM.com immediately at{' '}
              <a className="underline" href="mailto:privacy@atm.com">
                privacy@atm.com
              </a>{' '}
              and take appropriate steps to secure your Account.
            </li>
          </ul>
          <h2
            id="california"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            28. CALIFORNIA RESIDENTS
          </h2>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            28.1 DFPI Disclosure
          </h3>
          <p>
            ATM.com is not currently licensed by the California Department of
            Financial Protection and Innovation ("DFPI"). If the DFPI decides in
            the future to license ATM.com and/or requires modifications to
            ATM.com's cash advance product agreement, such developments may
            affect the terms under which the Services are offered but will not
            retroactively alter your existing obligations under this Agreement
            unless required by applicable law.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            28.2 California Consumer Privacy Act (CCPA/CPRA)
          </h3>
          <p>
            If you are a California resident, you have specific rights under the
            California Consumer Privacy Act of 2018, as amended by the
            California Privacy Rights Act of 2020 (collectively, the "CCPA"),
            with respect to your personal information. A full description of
            ATM.com's data collection practices, the categories of personal
            information we collect, the purposes for which we use it, the
            categories of third parties with whom we share it, and a complete
            description of your CCPA rights is set forth in our Privacy Policy,
            which is incorporated into these Terms by reference and is available
            at{' '}
            <Link href="/privacy-policy" target="_blank" className="underline">
              https://atm.com/privacy-policy
            </Link>
            .
          </p>
          <p>Your CCPA rights include, without limitation, the following:</p>
          <ul className="list-disc pl-6">
            <li>
              Right to Know. You have the right to request that ATM.com disclose
              the categories and specific pieces of personal information it has
              collected about you, the categories of sources from which that
              information was collected, the business or commercial purposes for
              collecting or selling your personal information, and the
              categories of third parties with whom ATM.com shares your personal
              information.
            </li>
            <li>
              Right to Delete. You have the right to request that ATM.com delete
              personal information it has collected from you, subject to certain
              exceptions permitted by law.
            </li>
            <li>
              Right to Correct. You have the right to request that ATM.com
              correct inaccurate personal information it maintains about you.
            </li>
            <li>
              Right to Opt Out of Sale or Sharing. You have the right to opt out
              of the sale or sharing of your personal information. As disclosed
              in Section 7 of these Terms, certain data sharing practices in
              connection with our CDLX partnership may constitute a sale or
              sharing of personal information under the CCPA. To exercise your
              right to opt out, click [Do Not Sell or Share My Personal
              Information] or contact us at{' '}
              <a href="mailto:privacy@atm.com" className="underline">
                privacy@atm.com
              </a>
              .
            </li>
            <li>
              Right to Limit Use of Sensitive Personal Information. You have the
              right to limit ATM.com's use and disclosure of sensitive personal
              information — including financial account information — to uses
              that are necessary to provide the Services, to the extent
              permitted by the CCPA.
            </li>
            <li>
              Right to Non-Discrimination. You have the right not to receive
              discriminatory treatment from ATM.com for exercising any of your
              CCPA rights. ATM.com will not deny you Services, charge you
              different prices, or provide you with a different level of service
              solely because you exercised a right under the CCPA.
            </li>
          </ul>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            28.3 How to Exercise Your California Privacy Rights
          </h3>
          <p>
            To submit a CCPA rights request, you may contact ATM.com by any of
            the following methods:
          </p>
          <ul className="list-disc pl-6">
            <li>
              Email:{' '}
              <a href="mailto:privacy@atm.com" className="underline">
                privacy@atm.com
              </a>
            </li>
            <li>
              Mail: ATM.com, Inc., P.O. Box 7475, Newport Beach, CA 92658-7475,
              Attn: Privacy Rights Request
            </li>
          </ul>
          <p>
            ATM.com will respond to verifiable consumer requests within the
            timeframes required by the CCPA — generally within forty-five (45)
            days of receipt, with the possibility of one forty-five (45) day
            extension where reasonably necessary. ATM.com may need to verify
            your identity before processing your request. We will not use
            personal information collected as part of the verification process
            for any purpose other than verification.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            28.4 Shine the Light
          </h3>
          <p>
            California Civil Code Section 1798.83 ("Shine the Light" law)
            permits California residents who have provided personal information
            to ATM.com to request certain information about ATM.com's disclosure
            of that information to third parties for those third parties' direct
            marketing purposes. If you are a California resident and wish to
            make such a request, please contact us at{' '}
            <a href="mailto:privacy@atm.com" className="underline">
              privacy@atm.com
            </a>{' '}
            or at the mailing address above.
          </p>
          <h3 className="font-display text-xl font-normal text-[#0a2d4a]">
            28.5 Relationship to Privacy Policy
          </h3>
          <p>
            This Section28 is intended as a summary of your California privacy
            rights and does not constitute ATM.com's complete CCPA disclosure.
            Our Privacy Policy contains ATM.com's full CCPA-compliant privacy
            notice, including all disclosures required by the CCPA and CPRA. In
            the event of any conflict between this Section 28 and the Privacy
            Policy with respect to California privacy rights, the Privacy Policy
            shall control. You are encouraged to review the Privacy Policy
            carefully and to contact us at{' '}
            <a href="mailto:privacy@atm.com" className="underline">
              privacy@atm.com
            </a>{' '}
            with any questions.
          </p>
          <h2
            id="survivability"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            29. SURVIVABILITY
          </h2>
          <p>The following sections survive account closure or termination:</p>
          <ul className="list-disc pl-6">
            <li>fees and payment obligations</li>
            <li>dispute resolution and arbitration</li>
            <li>limitation of liability</li>
            <li>indemnification</li>
            <li>regulatory compliance</li>
            <li>intellectual property rights</li>
            <li>privacy and data provisions</li>
            <li>any provision that by its nature should survive termination</li>
          </ul>
          <h2
            id="contact-information"
            className="font-display text-2xl font-normal text-[#0a2d4a] underline"
          >
            30. ATM.COM CONTACT INFORMATION
          </h2>
          <p>
            If you have any questions, comments, or concerns regarding these
            Terms or the Services, please contact us at{' '}
            <a href="mailto:support@atm.com" className="underline">
              support@atm.com
            </a>{' '}
            or ATM.com, Inc., P.O. Box 7475, Newport Beach, CA 92658-7475. When
            you email us, it is important to use the email address associated
            with your Account. If you don’t, we will need to verify your
            identity by alternative means.
          </p>

          {/* --------------------------------------------------------------------------------------------------- */}
        </div>
      </Container>
    </>
  )
}

Index.getLayout = (page: ReactElement) => (
  <MarketingLayout chin="white" mainClassName="-mt-[120px]">
    {page}
  </MarketingLayout>
)

export default Index
